import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import interact from 'interactjs';
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { Subject, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';

import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
  type BrandSummary,
} from '../../models/brand.models';
import {
  EDITABLE_DOCUMENT_STATUSES,
  type DocumentFieldDraft,
  type DocumentPayload,
  type PublicDocumentConfigSessionResponse,
  type DocumentStatus,
  type DocumentSummary,
} from '../../models/document.models';
import type {
  TemplateCheckboxSettings,
  TemplateDateSettings,
  TemplateDocumentSettings,
  TemplateDropdownSettings,
  TemplateFieldFontFamily,
  TemplateFieldSettings,
  TemplateFieldTabIndexMode,
  TemplateImageSettings,
  TemplateRadioSettings,
  TemplateSummary,
  TemplateTextboxSettings,
  TemplateUsageSettings,
  UploadedPdfFile,
} from '../../models/template.models';
import { TEMPLATE_FIELD_TYPES } from '../../models/template.models';
import { BrandApiService } from '../../services/brand-api.service';
import { DocumentApiService } from '../../services/document-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { TemplateApiService } from '../../services/template-api.service';
import { ToastService } from '../../services/toast.service';
import {
  FIELD_TIME_FORMAT_OPTIONS,
  USER_DATE_FORMAT_OPTIONS,
  UserDateTimeFormatService,
} from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';
import { FieldConfiguratorWorkspaceComponent } from '../../components/field-configurator/field-configurator-workspace.component';

const FALLBACK_BLANK_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iaiA8PC9UeXBlIC9DYXRhbG9nIC9QYWdlcyAyIDAgUj4+IGVuZG9iagoyIDAgb2JqIDw8L1R5cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDE+PiBlbmRvYmoKMyAwIG9iaiA8PC9UeXBlIC9QYWdlIC9QYXJlbnQgMiAwIFIgL01lZGlhQm94IFswIDAgNjEyIDc5Ml0gL1Jlc291cmNlcyA8PD4+Pj4gZW5kb2JqCnhyZWYKMCA0CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAxMCAwMDAwMCBuIAowMDAwMDAwMDYwIDAwMDAwIG4gCjAwMDAwMDAxMTcgMDAwMDAgbiAKdHJhaWxlciA8PC9TaXplIDQgL1Jvb3QgMSAwIFI+PgpzdGFydHhyZWYKMjAzCiUlRU9G';
import type {
  FieldConfiguratorActor,
  FieldConfiguratorOptionSets,
  FieldConfiguratorPreviewMeta,
  FieldConfiguratorSettingAction,
  FieldConfiguratorSettingsPanelOptions,
} from '../../components/field-configurator/field-configurator.models';
import {
  countFieldsForActor,
  fieldSupportsCharacterSpacing,
  filterFieldsForSummary,
  validateFieldConfiguratorStep,
} from '../../components/field-configurator/field-configurator.utils';

interface RecipientDraft {
  id: string;
  roleId: string;
  roleName: string;
  name: string;
  email: string;
  order: number;
  roleLocked: boolean;
}

type DocumentFieldType = DocumentFieldDraft['type'];
type PreviewRecipientFilter = 'all' | string;
type DocumentFieldSettings = TemplateFieldSettings;

interface FieldPaletteItem {
  type: DocumentFieldType;
  label: string;
  iconId: string;
}

interface ConfiguredDocumentField extends Omit<DocumentFieldDraft, 'settings'> {
  settings: DocumentFieldSettings;
}

type ScheduleQuickSlotKey = 'tomorrow_morning' | 'tomorrow_afternoon' | 'monday_morning';

interface ScheduleQuickSlot {
  key: ScheduleQuickSlotKey;
  label: string;
  atLocal: string;
  displayTime: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_LABELS: Record<DocumentFieldType, string> = {
  signature: 'Signature',
  initials: 'Initials',
  textbox: 'Textbox',
  date_signed: 'Date signed',
  checkbox: 'Checkbox',
  radio: 'Radio',
  name: 'Name',
  email: 'Email',
  title: 'Title',
  company: 'Company',
  editable_date: 'Editable Date',
  image: 'Image',
  dropdown: 'Dropdown',
};

const FIELD_ICON_IDS: Record<DocumentFieldType, string> = {
  signature: 'signature',
  initials: 'initials',
  textbox: 'textbox',
  date_signed: 'date',
  checkbox: 'checkbox',
  radio: 'radio',
  name: 'person',
  email: 'email',
  title: 'briefcase',
  company: 'briefcase',
  editable_date: 'date',
  image: 'image',
  dropdown: 'dropdown',
};

const FIELD_PALETTE: FieldPaletteItem[] = TEMPLATE_FIELD_TYPES.map((type) => ({
  type,
  label: FIELD_LABELS[type],
  iconId: FIELD_ICON_IDS[type],
}));

const FONT_FAMILY_OPTIONS: TemplateFieldFontFamily[] = [
  'Helvetica',
  'Courier',
  'Times New Roman',
  'Noto Sans',
  'Carlito',
];
const FONT_ALIGNMENT_OPTIONS = ['left', 'center', 'right'] as const;
const TEXTBOX_VALIDATION_OPTIONS = ['none', 'email', 'number', 'minimum', 'maximum', 'regex'] as const;
const TEXTBOX_VALIDATION_UI_OPTIONS = ['none', 'email', 'number', 'minimum', 'maximum'] as const;
const OPTION_VALIDATION_OPTIONS = ['none', 'minimum', 'maximum', 'exact'] as const;
const TEXTBOX_RESIZE_OPTIONS = [
  'grow_horizontally',
  'grow_vertically',
  'grow_both',
  'fixed',
  'auto_resize_font',
] as const;
const DATE_FORMAT_OPTIONS = USER_DATE_FORMAT_OPTIONS;
const TIME_FORMAT_OPTIONS = FIELD_TIME_FORMAT_OPTIONS;
const NON_RESIZABLE_FIELD_TYPES = new Set<DocumentFieldType>([
  'date_signed',
  'checkbox',
  'radio',
  'name',
  'email',
  'editable_date',
]);
const LEGACY_AUTO_GROUP_KEY_PATTERN = /^(?:checkbox|radio)-group-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const DEFAULT_SENDER_ACTIONS = {
  allowEditing: true,
  allowDeletion: true,
  requireDefaultValue: false,
} as const;

const DEFAULT_TEMPLATE_USAGE_SETTINGS: TemplateUsageSettings = {
  allowAddMoreFiles: true,
  allowReplaceRearrangeDeleteFiles: true,
  allowAddMoreRecipients: true,
  allowEditDocumentMessage: true,
  allowAddNewFields: true,
  allowEditOrDeleteFields: true,
};

const DEFAULT_FONT_SETTINGS = {
  family: 'Helvetica',
  size: 13,
  lineHeight: 15,
  bold: false,
  italic: false,
  underline: false,
  color: '#000000',
  align: 'left',
  rtl: false,
  characterSpacing: 0,
} as const;

const ROLE_COLOR_PALETTE = ['#7E21D9', '#2563EB', '#059669', '#D97706', '#DB2777', '#0891B2'];
const ROLE_COLOR_GOLDEN_ANGLE_DEGREES = 137.508;
const ROLE_COLOR_HUE_OFFSET_DEGREES = 24;
const DEFAULT_MIN_SCHEDULE_LEAD_MINUTES = 30;
const SCHEDULE_REQUIRED_ERROR = 'Select date and time for schedule send.';
const SCHEDULE_EXPIRY_ERROR = 'Scheduled send time must be before expiry';

const trimOrUndefined = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const sanitizeOptionValues = (values: string[], fallback: string): string[] => {
  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    sanitized.push(trimmed);
  }

  if (sanitized.length === 0) {
    return [fallback];
  }

  return sanitized;
};

const clampIntegerOrUndefined = (value: number | undefined): number | undefined => {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }

  return Math.trunc(value);
};

const defaultFieldKeyPrefix = (type: DocumentFieldType): string => {
  switch (type) {
    case 'date_signed':
      return 'DateSigned';
    case 'editable_date':
      return 'EditableDate';
    default:
      return FIELD_LABELS[type].replace(/\s+/g, '');
  }
};

const clampNormalizedSize = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const textWidthToRect = (
  text: string,
  options: { min: number; max: number; base: number; perChar: number },
): number => {
  const normalized = options.base + text.length * options.perChar;
  return clampNormalizedSize(normalized, options.min, options.max);
};
const COMPACT_FIELD_HEIGHT = 0.04;
const MIN_MEASURED_FIELD_WIDTH = 0.015;
const MAX_MEASURED_FIELD_WIDTH = 0.45;
const MIN_MEASURED_FIELD_HEIGHT = 0.015;
const MAX_MEASURED_FIELD_HEIGHT = 0.16;
const MEASURED_FIELD_SAFETY_PX = 0;

const createRecipient = (roleName: string, roleLocked = false): RecipientDraft => {
  const id = `recipient-${crypto.randomUUID()}`;
  return {
    id,
    roleId: id,
    roleName,
    name: '',
    email: '',
    order: 1,
    roleLocked,
  };
};

const createDefaultTextboxSettings = (): TemplateTextboxSettings => ({
  readOnly: false,
  maskValue: false,
  defaultText: undefined,
  hintText: undefined,
  resizeType: 'grow_vertically',
  fixedWidth: false,
  characterLimit: undefined,
  validationType: 'none',
  validationValue: undefined,
});

const createDefaultDropdownSettings = (): TemplateDropdownSettings => ({
  hintText: undefined,
  options: ['Option1'],
  defaultOption: undefined,
});

const createDefaultCheckboxSettings = (): TemplateCheckboxSettings => ({
  optionGroupId: undefined,
  value: 'Checkbox1',
  checked: false,
  size: 16,
  values: ['Checkbox1'],
  defaultValues: [],
});

const createDefaultRadioSettings = (): TemplateRadioSettings => ({
  groupName: undefined,
  value: 'Radio1',
  checked: false,
  size: 16,
  values: ['Radio1'],
  defaultValue: undefined,
});

const createDefaultDateSettings = (): TemplateDateSettings => ({
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'None',
  defaultDate: undefined,
});

const createDefaultImageSettings = (): TemplateImageSettings => ({
  title: undefined,
  instruction: undefined,
  acceptJpgJpeg: true,
  acceptSvg: true,
  acceptPng: true,
  acceptBmp: true,
});

const createDefaultFieldSettings = (): DocumentFieldSettings => ({
  common: {
    senderActions: { ...DEFAULT_SENDER_ACTIONS },
    dataSyncTag: undefined,
    tabIndexMode: 'auto',
    tabIndex: undefined,
    autoPositioningEnabled: false,
  },
  font: { ...DEFAULT_FONT_SETTINGS },
  textbox: createDefaultTextboxSettings(),
  dropdown: createDefaultDropdownSettings(),
  checkbox: createDefaultCheckboxSettings(),
  radio: createDefaultRadioSettings(),
  date: createDefaultDateSettings(),
  image: createDefaultImageSettings(),
});

const normalizeFieldSettings = (settings: unknown): DocumentFieldSettings => {
  const defaults = createDefaultFieldSettings();
  const input = (settings && typeof settings === 'object' ? settings : {}) as Partial<DocumentFieldSettings>;
  const commonRaw = input.common;
  const tabIndexMode: TemplateFieldTabIndexMode = commonRaw?.tabIndexMode === 'custom' ? 'custom' : 'auto';
  const tabIndex = tabIndexMode === 'custom' ? clampIntegerOrUndefined(commonRaw?.tabIndex) : undefined;

  const dropdownOptions = sanitizeOptionValues(input.dropdown?.options ?? defaults.dropdown.options, 'Option1');
  const checkboxValues = sanitizeOptionValues(input.checkbox?.values ?? [], 'Checkbox1');
  const radioValues = sanitizeOptionValues(input.radio?.values ?? [], 'Radio1');
  const checkboxValue = trimOrUndefined(input.checkbox?.value) ?? checkboxValues[0] ?? defaults.checkbox.value;
  const radioValue = trimOrUndefined(input.radio?.value) ?? radioValues[0] ?? defaults.radio.value;

  return {
    common: {
      senderActions: {
        allowEditing: commonRaw?.senderActions?.allowEditing ?? defaults.common.senderActions.allowEditing,
        allowDeletion: commonRaw?.senderActions?.allowDeletion ?? defaults.common.senderActions.allowDeletion,
        requireDefaultValue:
          commonRaw?.senderActions?.requireDefaultValue ?? defaults.common.senderActions.requireDefaultValue,
      },
      dataSyncTag: trimOrUndefined(commonRaw?.dataSyncTag),
      tabIndexMode,
      tabIndex,
      autoPositioningEnabled: commonRaw?.autoPositioningEnabled ?? defaults.common.autoPositioningEnabled,
    },
    font: {
      family:
        input.font?.family && FONT_FAMILY_OPTIONS.includes(input.font.family)
          ? input.font.family
          : defaults.font.family,
      size: clampIntegerOrUndefined(input.font?.size) ?? defaults.font.size,
      lineHeight: clampIntegerOrUndefined(input.font?.lineHeight) ?? defaults.font.lineHeight,
      bold: input.font?.bold ?? defaults.font.bold,
      italic: input.font?.italic ?? defaults.font.italic,
      underline: input.font?.underline ?? defaults.font.underline,
      color: trimOrUndefined(input.font?.color) ?? defaults.font.color,
      align:
        input.font?.align && FONT_ALIGNMENT_OPTIONS.includes(input.font.align) ? input.font.align : defaults.font.align,
      rtl: input.font?.rtl ?? defaults.font.rtl,
      characterSpacing: clampIntegerOrUndefined(input.font?.characterSpacing) ?? defaults.font.characterSpacing,
    },
    textbox: {
      ...defaults.textbox,
      ...input.textbox,
      resizeType:
        input.textbox?.resizeType && TEXTBOX_RESIZE_OPTIONS.includes(input.textbox.resizeType)
          ? input.textbox.resizeType
          : defaults.textbox.resizeType,
      validationType:
        input.textbox?.validationType && TEXTBOX_VALIDATION_OPTIONS.includes(input.textbox.validationType)
          ? input.textbox.validationType
          : defaults.textbox.validationType,
      defaultText: trimOrUndefined(input.textbox?.defaultText),
      hintText: trimOrUndefined(input.textbox?.hintText),
      characterLimit: clampIntegerOrUndefined(input.textbox?.characterLimit),
      validationValue: trimOrUndefined(input.textbox?.validationValue),
    },
    dropdown: {
      ...defaults.dropdown,
      ...input.dropdown,
      hintText: trimOrUndefined(input.dropdown?.hintText),
      options: dropdownOptions,
      defaultOption:
        trimOrUndefined(input.dropdown?.defaultOption) &&
        dropdownOptions.includes(trimOrUndefined(input.dropdown?.defaultOption)!)
          ? trimOrUndefined(input.dropdown?.defaultOption)
          : undefined,
    },
    checkbox: {
      ...defaults.checkbox,
      ...input.checkbox,
      optionGroupId: trimOrUndefined(input.checkbox?.optionGroupId),
      value: checkboxValue,
      values: checkboxValues,
      defaultValues: sanitizeOptionValues(input.checkbox?.defaultValues ?? [], ''),
      checked:
        input.checkbox?.checked ??
        sanitizeOptionValues(input.checkbox?.defaultValues ?? [], '').includes(checkboxValue),
      size: clampIntegerOrUndefined(input.checkbox?.size) ?? defaults.checkbox.size,
    },
    radio: {
      ...defaults.radio,
      ...input.radio,
      groupName: trimOrUndefined(input.radio?.groupName),
      value: radioValue,
      values: radioValues,
      defaultValue: trimOrUndefined(input.radio?.defaultValue),
      checked: input.radio?.checked ?? trimOrUndefined(input.radio?.defaultValue) === radioValue,
      size: clampIntegerOrUndefined(input.radio?.size) ?? defaults.radio.size,
    },
    date: {
      ...defaults.date,
      ...input.date,
      defaultDate: trimOrUndefined(input.date?.defaultDate),
    },
    image: {
      ...defaults.image,
      ...input.image,
      title: trimOrUndefined(input.image?.title),
      instruction: trimOrUndefined(input.image?.instruction),
    },
  };
};

const normalizeFieldSettingsForType = (
  fieldType: DocumentFieldType,
  settings: unknown,
): DocumentFieldSettings => {
  const normalized = normalizeFieldSettings(settings);
  if (fieldSupportsCharacterSpacing(fieldType)) {
    return normalized;
  }

  return {
    ...normalized,
    font: {
      ...normalized.font,
      characterSpacing: 0,
    },
  };
};

@Component({
  selector: 'app-document-create',
  imports: [CommonModule, FormsModule, DragDropModule, NgxExtendedPdfViewerModule, FieldConfiguratorWorkspaceComponent],
  templateUrl: './document-create.component.html',
  styleUrl: './document-create.component.scss',
})
export class DocumentCreateComponent implements OnInit, OnDestroy {
  @ViewChild('viewerHost', { read: ElementRef }) private readonly viewerHost?: ElementRef<HTMLElement>;

  currentStep: 1 | 2 = 1;
  stepTwoMode: 'configure' | 'preview' = 'configure';
  private nextGeneratedRecipientRoleIndex = 1;

  sourceType: 'upload' | 'template' = 'upload';
  selectedFile: File | null = null;
  selectedTemplateId = '';
  selectedTemplate: TemplateSummary | null = null;
  uploadedSourceFile: UploadedPdfFile | null = null;
  editingDocumentId: string | null = null;
  editingDocumentStatus: DocumentStatus | null = null;
  publicConfigToken: string | null = null;
  publicConfigDocumentId: string | null = null;
  publicConfigCanEditFields = true;
  publicConfigInitializing = false;
  editingSourceTemplateId: string | null = null;
  sourceTemplateUsageSettings: TemplateUsageSettings | null = null;
  sourceFileReplaced = false;
  loadingDocumentForEdit = false;
  editLoadError = '';
  savingChanges = false;
  publicConfigSaveMessage = '';
  publicConfigToastMessage = '';

  templates: TemplateSummary[] = [];
  templatesLoading = false;
  templateModalOpen = false;
  templateSearchTerm = '';
  templateSearchError = '';
  templateUseInProgressId = '';

  recipients: RecipientDraft[] = [this.createGeneratedRecipient()];
  fields: DocumentFieldDraft[] = [];

  activeRecipientRoleId = '';
  previewRecipientFilter: PreviewRecipientFilter = 'all';
  activePage = 1;
  pageCount = 1;
  selectedFieldId: string | null = null;
  dragFieldType: DocumentFieldType | null = null;
  pdfLoading = false;
  pdfLoaded = false;
  pdfLoadError = '';
  viewerMounted = false;
  viewerRotation: 0 | 90 | 180 | 270 = 0;

  signingOrderEnabled = false;
  cc = '';
  tags = '';
  documentTitle = '';
  documentMessage = '';
  expiryAtLocal = '';
  documentSettings: DocumentPayload['documentSettings'] = {
    brandId: undefined,
    brandName: undefined,
    allowPrintAndSign: false,
  };
  brands: BrandSummary[] = [];
  brandsLoading = false;

  sending = false;
  sendOptionsOpen = false;
  loadingTemplate = false;
  schedulePickerOpen = false;
  scheduleDateTimeModalOpen = false;
  scheduleConfirmModalOpen = false;
  scheduleDateInput = '';
  scheduleTimeInput = '';
  pendingScheduleAtLocal = '';
  readonly minScheduleLeadMinutes = DEFAULT_MIN_SCHEDULE_LEAD_MINUTES;

  stepOneSubmitted = false;
  stepTwoSubmitted = false;
  formError = '';

  previewUrl: string | null = null;

  readonly fieldPalette = FIELD_PALETTE;
  readonly fieldTypeOptions = TEMPLATE_FIELD_TYPES;
  readonly templateSearchDebounceMs = 300;
  readonly fontFamilyOptions = FONT_FAMILY_OPTIONS;
  readonly fontAlignmentOptions = FONT_ALIGNMENT_OPTIONS;
  readonly textboxValidationOptions = TEXTBOX_VALIDATION_UI_OPTIONS;
  readonly optionValidationOptions = OPTION_VALIDATION_OPTIONS;
  readonly textboxResizeOptions = TEXTBOX_RESIZE_OPTIONS;
  readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  readonly timeFormatOptions = TIME_FORMAT_OPTIONS;

  private overlaySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private fieldRectsNormalizedForSession = false;
  private interactingFieldId: string | null = null;
  private interactingOverlayPage: number | null = null;
  private readonly roleColorRegistry = new Map<string, string>();
  private roleColorGeneratedIndex = 0;
  private readonly releasedGeneratedRoleColors: string[] = [];

  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly templateApi = inject(TemplateApiService);
  private readonly brandApi = inject(BrandApiService);
  private readonly documentApi = inject(DocumentApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly zone = inject(NgZone);
  private readonly userDateTimeFormat = inject(UserDateTimeFormatService);
  private readonly toast = inject(ToastService);
  private readonly templateSearchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private templateSearchRequestId = 0;
  private previousDisableHistory = pdfDefaultOptions.disableHistory;
  private selectedConfiguredFieldCache:
    | { source: DocumentFieldDraft; value: ConfiguredDocumentField }
    | null = null;

  get selectedField(): ConfiguredDocumentField | null {
    if (!this.selectedFieldId) {
      this.selectedConfiguredFieldCache = null;
      return null;
    }

    const field = this.fields.find((entry) => entry.id === this.selectedFieldId);
    if (!field) {
      this.selectedConfiguredFieldCache = null;
      return null;
    }

    if (this.selectedConfiguredFieldCache?.source === field) {
      return this.selectedConfiguredFieldCache.value;
    }

    const configured = this.toConfiguredField(field);
    this.selectedConfiguredFieldCache = { source: field, value: configured };
    return configured;
  }

  get selectedFieldSettingsTitle(): string {
    const field = this.selectedField;
    if (!field) {
      return 'Field settings';
    }

    return `${FIELD_LABELS[field.type]} settings`;
  }

  get fieldConfiguratorActors(): FieldConfiguratorActor[] {
    return this.recipients.map((recipient) => ({
      id: recipient.roleId,
      label: this.recipientDisplayLabel(recipient),
      kind: 'recipient',
      color: this.roleChipColor(recipient.roleId),
      meta: {
        name: recipient.name || undefined,
        email: recipient.email || undefined,
        order: recipient.order,
        locked: recipient.roleLocked,
      },
    }));
  }

  get fieldConfiguratorPreviewMeta(): FieldConfiguratorPreviewMeta {
    return {
      fileLabel: this.selectedFile?.name ?? this.uploadedSourceFile?.originalName ?? '',
      title: this.documentTitle,
      message: this.documentMessage,
      canEditTitle: !this.isPublicConfigureMode,
      canEditMessage: !this.isPublicConfigureMode && this.canEditDocumentMessage,
    };
  }


  get fieldConfiguratorSettingsPanelOptions(): FieldConfiguratorSettingsPanelOptions {
    return {
      actorLabel: 'Assigned to',
      pageMetaLabel: 'Page',
      fieldsetDisabled: !this.canEditOrDeleteFieldsInEditor,
      showDefaultDropdownOption: false,
      showImageAcceptOptions: false,
      showAddDefaultImageButton: false,
      showAutoPositionConfigureButton: false,
      showConditionalLogicSection: false,
    };
  }

  readonly fieldConfiguratorOptionSets: FieldConfiguratorOptionSets = {
    fontFamilyOptions: this.fontFamilyOptions,
    fontAlignmentOptions: this.fontAlignmentOptions,
    textboxResizeOptions: this.textboxResizeOptions,
    textboxValidationOptions: this.textboxValidationOptions,
    dateFormatOptions: this.dateFormatOptions,
    timeFormatOptions: this.timeFormatOptions,
  };

  get canPlaceFields(): boolean {
    return this.stepTwoMode === 'configure' && this.pdfLoaded && !this.pdfLoading && !this.pdfLoadError && this.canAddFieldsInEditor;
  }

  get viewerPdfSource(): string | null {
    return this.previewUrl;
  }

  get viewerPageBinding(): number | undefined {
    return this.pdfLoaded ? this.activePage : undefined;
  }

  get scheduleTimezoneLabel(): string {
    return this.userDateTimeFormat.timeZoneLabel('long');
  }

  get scheduleQuickSlots(): ScheduleQuickSlot[] {
    const today = this.currentScheduleDate();
    const tomorrow = this.shiftDateParts(today, 1);
    const daysUntilMonday = (8 - this.weekdayFromDateParts(today)) % 7 || 7;
    const monday = this.shiftDateParts(today, daysUntilMonday);

    const slots: Array<{ key: ScheduleQuickSlotKey; label: string; at: Date }> = [
      {
        key: 'tomorrow_morning',
        label: 'Tomorrow morning',
        at: this.scheduleDateForParts(tomorrow, 9, 0),
      },
      {
        key: 'tomorrow_afternoon',
        label: 'Tomorrow afternoon',
        at: this.scheduleDateForParts(tomorrow, 13, 0),
      },
      {
        key: 'monday_morning',
        label: 'Monday morning',
        at: this.scheduleDateForParts(monday, 9, 0),
      },
    ];

    return slots.map((slot) => ({
      key: slot.key,
      label: slot.label,
      atLocal: this.toLocalDateTimeInput(slot.at),
      displayTime: this.formatScheduleSlotTime(slot.at),
    }));
  }

  get isSendActionDisabled(): boolean {
    return this.sending || this.currentStep !== 2 || this.publicConfigInitializing;
  }

  get isEditMode(): boolean {
    return !!this.editingDocumentId;
  }

  get isPublicConfigureMode(): boolean {
    return !!this.publicConfigToken;
  }

  get pageHeading(): string {
    if (this.isPublicConfigureMode) {
      return this.publicConfigCanEditFields ? 'Configure document fields' : 'View configured document fields';
    }

    return this.isEditMode
      ? this.currentStep === 1
        ? 'Edit document'
        : 'Update document fields'
      : this.currentStep === 1
        ? 'Prepare document for signing'
        : 'Configure fields';
  }

  get canSendEditedDraft(): boolean {
    return this.isEditMode && this.editingDocumentStatus === 'draft';
  }

  get showSaveAction(): boolean {
    if (this.isPublicConfigureMode) {
      return this.currentStep === 2 && this.publicConfigCanEditFields && !this.publicConfigInitializing;
    }

    return this.isEditMode || this.currentStep === 2;
  }

  get showSendAction(): boolean {
    if (this.isPublicConfigureMode) {
      return this.currentStep === 2 && this.publicConfigCanEditFields && !this.publicConfigInitializing;
    }

    return !this.isEditMode || this.canSendEditedDraft;
  }

  get editUsageSettings(): TemplateUsageSettings {
    return this.sourceTemplateUsageSettings ?? DEFAULT_TEMPLATE_USAGE_SETTINGS;
  }

  get canReplaceSourceFile(): boolean {
    if (!this.isEditMode) {
      return false;
    }

    if (this.sourceType !== 'template') {
      return true;
    }

    return this.editUsageSettings.allowReplaceRearrangeDeleteFiles;
  }

  get canManageRecipients(): boolean {
    if (this.isPublicConfigureMode) {
      return false;
    }

    return !this.isEditMode || this.sourceType !== 'template' || this.editUsageSettings.allowAddMoreRecipients;
  }

  get canEditDocumentMessage(): boolean {
    if (this.isPublicConfigureMode) {
      return false;
    }

    return !this.isEditMode || this.sourceType !== 'template' || this.editUsageSettings.allowEditDocumentMessage;
  }

  get canAddFieldsInEditor(): boolean {
    if (this.isPublicConfigureMode) {
      return this.publicConfigCanEditFields;
    }

    return !this.isEditMode || this.sourceType !== 'template' || this.editUsageSettings.allowAddNewFields;
  }

  get canEditOrDeleteFieldsInEditor(): boolean {
    if (this.isPublicConfigureMode) {
      return this.publicConfigCanEditFields;
    }

    return !this.isEditMode || this.sourceType !== 'template' || this.editUsageSettings.allowEditOrDeleteFields;
  }

  get isSaveActionDisabled(): boolean {
    return this.savingChanges || this.loadingDocumentForEdit || this.publicConfigInitializing;
  }

  private canMutateFieldStructure(): boolean {
    return this.canAddFieldsInEditor;
  }

  private canMutateExistingFields(): boolean {
    return this.canEditOrDeleteFieldsInEditor;
  }

  ngOnInit(): void {
    this.previousDisableHistory = pdfDefaultOptions.disableHistory;
    pdfDefaultOptions.disableHistory = true;

    this.templateSearchInput$
      .pipe(
        debounceTime(this.templateSearchDebounceMs),
        map((value) => value.trim()),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.loadTemplates(value);
      });

    this.syncRoleColorRegistry();

    const publicConfigToken = this.route.snapshot.routeConfig?.path === 'developers/api/configure-fields/:token'
      ? this.route.snapshot.paramMap.get('token')
      : null;
    if (publicConfigToken) {
      this.publicConfigToken = publicConfigToken;
      this.currentStep = 2;
      this.publicConfigInitializing = true;
      this.loadPublicConfigSession(publicConfigToken);
      return;
    }

    this.loadBrands();

    const documentId = this.route.snapshot.paramMap.get('id');
    if (documentId) {
      this.editingDocumentId = documentId;
      this.loadDocumentForEdit(documentId);
    }
  }

  ngOnDestroy(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
      this.overlaySyncTimer = null;
    }

    this.clearFieldInteractionState();
    this.viewerHost?.nativeElement
      .querySelectorAll<HTMLElement>('.template-field')
      .forEach((element) => interact(element).unset());

    this.destroy$.next();
    this.destroy$.complete();
    this.templateSearchInput$.complete();
    this.releasePreviewUrl();
    pdfDefaultOptions.disableHistory = this.previousDisableHistory;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.scheduleConfirmModalOpen) {
      this.closeScheduleConfirmModal();
      return;
    }

    if (this.scheduleDateTimeModalOpen) {
      this.closeScheduleDateTimeModal();
      return;
    }

    if (this.schedulePickerOpen) {
      this.closeSchedulePicker();
      return;
    }

    if (this.sendOptionsOpen) {
      this.closeSendOptions();
      this.cdr.markForCheck();
      return;
    }

    if (this.templateModalOpen) {
      this.closeTemplateModal();
      return;
    }

    if (this.currentStep === 2 && this.stepTwoMode === 'configure' && this.selectedFieldId) {
      this.clearSelectedField();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.currentStep !== 2 || this.stepTwoMode !== 'configure' || !this.selectedFieldId || !this.canMutateExistingFields()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      this.removeSelectedField();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sendOptionsOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      this.closeSendOptions();
      this.cdr.markForCheck();
      return;
    }

    const splitSendElement = this.hostElement.nativeElement.querySelector('.split-send');
    if (splitSendElement?.contains(target)) {
      return;
    }

    this.closeSendOptions();
    this.cdr.markForCheck();
  }

  goBack(): void {
    if (this.isPublicConfigureMode) {
      if (window.parent && window.parent !== window) {
        return;
      }

      void this.router.navigate(['/developers/api/reference']);
      return;
    }

    void this.router.navigate(['/documents']);
  }

  onSourceTypeChange(nextType: 'upload' | 'template'): void {
    if (this.isEditMode) {
      return;
    }

    if (this.sourceType === nextType) {
      return;
    }

    this.sourceType = nextType;
    this.selectedFile = null;
    this.selectedTemplateId = '';
    this.selectedTemplate = null;
    this.uploadedSourceFile = null;
    this.fields = [];
    this.nextGeneratedRecipientRoleIndex = 1;
    this.recipients = [this.createGeneratedRecipient()];
    this.templateModalOpen = false;
    this.templateUseInProgressId = '';
    this.templateSearchError = '';
    this.formError = '';
    this.resetStepTwoUiState();
    this.syncRoleColorRegistry();
    this.releasePreviewUrl();
    this.cdr.markForCheck();
  }

  activateUploadSource(fileInput: HTMLInputElement): void {
    if ((this.isEditMode && !this.canReplaceSourceFile) || (this.selectedFile && !this.isEditMode)) {
      return;
    }

    if (this.sourceType !== 'upload') {
      this.onSourceTypeChange('upload');
    }

    fileInput.value = '';
    fileInput.click();
  }

  activateTemplateSource(): void {
    if (this.isEditMode || this.selectedFile) {
      return;
    }

    if (this.sourceType !== 'template') {
      this.onSourceTypeChange('template');
    }

    this.openTemplateModal();
  }

  clearSourceSelection(): void {
    if (this.isEditMode) {
      return;
    }

    this.selectedFile = null;
    this.selectedTemplateId = '';
    this.selectedTemplate = null;
    this.uploadedSourceFile = null;
    this.fields = [];
    this.templateModalOpen = false;
    this.templateUseInProgressId = '';
    this.formError = '';
    this.resetStepTwoUiState();
    this.releasePreviewUrl();
    this.cdr.markForCheck();
  }

  openTemplateModal(): void {
    if (this.selectedFile) {
      return;
    }

    const shouldLoadTemplates = this.templates.length === 0 || this.templateSearchError.length > 0;
    this.templateModalOpen = true;
    this.templateSearchError = '';

    if (shouldLoadTemplates) {
      this.loadTemplates(this.templateSearchTerm.trim());
    }

    this.cdr.markForCheck();
  }

  closeTemplateModal(): void {
    if (!this.templateModalOpen) {
      return;
    }

    this.templateModalOpen = false;
    this.templateUseInProgressId = '';
    this.cdr.markForCheck();
  }

  onTemplateModalMaskClick(event: MouseEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }

    this.closeTemplateModal();
  }

  onTemplateSearchInput(value: string): void {
    this.templateSearchTerm = value;
    this.templateSearchInput$.next(value);
  }

  retryTemplateSearch(): void {
    this.loadTemplates(this.templateSearchTerm.trim());
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);

    if (!file) {
      return;
    }

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.formError = 'Only PDF files are allowed.';
      this.selectedFile = null;
      input.value = '';
      this.cdr.markForCheck();
      return;
    }

    this.formError = '';
    this.selectedFile = file;
    this.sourceFileReplaced = this.isEditMode;
    if (!this.documentTitle.trim()) {
      this.documentTitle = this.titleFromUploadedFileName(file.name);
    }

    if (!this.isEditMode) {
      this.selectedTemplate = null;
      this.selectedTemplateId = '';
      this.uploadedSourceFile = null;
      this.fields = [];
      this.resetStepTwoUiState();
    } else {
      this.uploadedSourceFile = null;
      this.viewerMounted = false;
      this.pdfLoading = false;
      this.pdfLoaded = false;
      this.pdfLoadError = '';
      this.formError = '';
    }

    this.viewerRotation = 0;
    this.releasePreviewUrl();
    this.previewUrl = URL.createObjectURL(file);
    this.cdr.markForCheck();
  }

  private titleFromUploadedFileName(fileName: string): string {
    return fileName.replace(/\.pdf$/i, '');
  }

  useTemplate(templateId: string): void {
    if (this.isEditMode || this.selectedFile || this.loadingTemplate) {
      return;
    }

    this.selectedTemplateId = templateId;
    this.templateUseInProgressId = templateId;
    this.loadingTemplate = true;
    this.formError = '';
    this.loadSelectedTemplate();
  }

  getTemplateRolesLabel(template: TemplateSummary): string {
    if (!template.roles.length) {
      return 'No roles';
    }

    return template.roles.map((role) => role.name).join(', ');
  }

  private loadSelectedTemplate(): void {
    if (!this.selectedTemplateId) {
      this.loadingTemplate = false;
      this.templateUseInProgressId = '';
      this.cdr.markForCheck();
      return;
    }

    this.templateApi
      .getTemplate(this.selectedTemplateId)
      .pipe(
        switchMap((template) =>
          forkJoin({
            template: of(template),
            pdfBlob: this.templateApi.downloadPdf(template.sourceFile.fileId),
          }),
        ),
        finalize(() => {
          this.loadingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ template, pdfBlob }) => {
          this.selectedTemplate = template;
          this.selectedFile = new File([pdfBlob], template.sourceFile.originalName, {
            type: 'application/pdf',
            lastModified: Date.now(),
          });
          this.uploadedSourceFile = template.sourceFile;
          this.signingOrderEnabled = template.signingOrderEnabled;
          this.cc = template.ccEmails.join(', ');
          this.tags = template.tags.join(', ');
          this.documentTitle = template.document.title;
          this.documentMessage = template.document.message ?? '';
          this.documentSettings = this.normalizedDocumentSettings(template.documentSettings);
          this.ensureBrandSelection(this.documentSettings);
          this.sourceTemplateUsageSettings = null;
          this.editingSourceTemplateId = null;
          this.sourceFileReplaced = false;
          this.recipients = template.roles.map((role, index) => ({
            id: role.id,
            roleId: role.id,
            roleName: role.name,
            name: role.defaultRecipientName ?? '',
            email: role.defaultRecipientEmail ?? '',
            order: index + 1,
            roleLocked: true,
          }));
          this.nextGeneratedRecipientRoleIndex = template.roles.length + 1;
          if (this.recipients.length === 0) {
            this.nextGeneratedRecipientRoleIndex = 1;
            this.recipients = [this.createGeneratedRecipient()];
          } else {
            this.reindexRecipients();
          }

          const fieldKeyCounts = new Map<string, number>();
          const templateFields = template.fields.map((field) => {
            const prefix = defaultFieldKeyPrefix(field.type);
            const nextCount = (fieldKeyCounts.get(prefix) ?? 0) + 1;
            fieldKeyCounts.set(prefix, nextCount);

            return {
              id: field.id,
              fieldKey: trimOrUndefined(field.fieldKey) ?? `${prefix}${nextCount}`,
              type: field.type,
              role: field.role,
              page: Math.max(1, Math.trunc(field.page || 1)),
              rect: { ...field.rect },
              required: field.required,
              label: trimOrUndefined(field.label),
              placeholder: trimOrUndefined(field.placeholder),
              settings: normalizeFieldSettingsForType(field.type, field.settings),
            };
          });
          this.fields = this.dedupeFieldsByIdKeepLast(templateFields);
          this.normalizeChoiceGroupMetadata();

          this.syncRoleColorRegistry();
          this.resetStepTwoUiState();
          this.viewerRotation = 0;
          this.releasePreviewUrl();
          this.previewUrl = URL.createObjectURL(pdfBlob);
          this.feedback.clearError();
          this.formError = '';
          this.templateSearchError = '';
          this.templateModalOpen = false;
          this.templateUseInProgressId = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load template',
          });
          this.formError = normalized.message;
          this.feedback.showError({ ...normalized });
          this.templateUseInProgressId = '';
          this.cdr.markForCheck();
        },
      });
  }

  private loadDocumentForEdit(documentId: string): void {
    this.loadingDocumentForEdit = true;
    this.editLoadError = '';
    this.formError = '';

    this.documentApi
      .getDocument(documentId)
      .pipe(
        switchMap((document) => {
          if (!EDITABLE_DOCUMENT_STATUSES.includes(document.status)) {
            throw new Error('Only draft, scheduled, needs attention, and waiting for others documents can be edited.');
          }

          return forkJoin({
            document: of(document),
            pdfBlob: this.templateApi.downloadPdf(document.sourceFile.fileId),
          });
        }),
        finalize(() => {
          this.loadingDocumentForEdit = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ document, pdfBlob }) => {
          this.feedback.clearError();
          this.hydrateFromDocument(document, pdfBlob);
          this.editLoadError = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load document for editing',
          });
          this.editLoadError = normalized.message;
          this.formError = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.loadDocumentForEdit(documentId),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private hydrateFromDocument(document: DocumentSummary, pdfBlob: Blob): void {
    const lastModified = Number.isNaN(Date.parse(document.updatedAt))
      ? Date.now()
      : new Date(document.updatedAt).getTime();

    this.editingDocumentId = document._id;
    this.editingDocumentStatus = document.status;
    this.editingSourceTemplateId = document.sourceTemplateId ?? document.templateId ?? null;
    this.sourceType = document.sourceType;
    this.selectedTemplateId = this.editingSourceTemplateId ?? '';
    this.selectedTemplate = null;
    this.selectedFile = new File([pdfBlob], document.sourceFile.originalName, {
      type: 'application/pdf',
      lastModified,
    });
    this.uploadedSourceFile = { ...document.sourceFile };
    this.sourceTemplateUsageSettings = document.sourceType === 'template'
      ? { ...(document.sourceTemplateUsageSettings ?? DEFAULT_TEMPLATE_USAGE_SETTINGS) }
      : null;
    this.sourceFileReplaced = false;
    this.signingOrderEnabled = document.signingOrderEnabled;
    this.cc = document.ccEmails.join(', ');
    this.tags = document.tags.join(', ');
    this.documentTitle = document.document.title;
    this.documentMessage = document.document.message ?? '';
    this.documentSettings = this.normalizedDocumentSettings(document.documentSettings);
    this.ensureBrandSelection(this.documentSettings);
    this.expiryAtLocal = this.toLocalDateTimeForInput(document.expiryAt);
    this.recipients = document.recipients.map((recipient, index) => ({
      id: recipient.id,
      roleId: recipient.roleId,
      roleName: recipient.roleName,
      name: recipient.name,
      email: recipient.email,
      order: index + 1,
      roleLocked: false,
    }));

    if (this.recipients.length === 0) {
      this.nextGeneratedRecipientRoleIndex = 1;
      this.recipients = [this.createGeneratedRecipient()];
    } else {
      this.nextGeneratedRecipientRoleIndex = this.recipients.length + 1;
      this.reindexRecipients();
    }

    this.fields = this.dedupeFieldsByIdKeepLast(
      document.fields.map((field) => ({
        ...field,
        page: Math.max(1, Math.trunc(field.page || 1)),
        rect: { ...field.rect },
        fieldKey: trimOrUndefined(field.fieldKey) ?? field.fieldKey,
        label: trimOrUndefined(field.label),
        placeholder: trimOrUndefined(field.placeholder),
        settings: normalizeFieldSettingsForType(field.type, field.settings),
      })),
    );
    this.normalizeChoiceGroupMetadata();

    this.stepOneSubmitted = false;
    this.stepTwoSubmitted = false;
    this.currentStep = 1;
    this.stepTwoMode = 'configure';
    this.formError = '';
    this.editLoadError = '';
    this.loadingTemplate = false;
    this.templateModalOpen = false;
    this.templateUseInProgressId = '';
    this.activeRecipientRoleId = this.recipients[0]?.roleId ?? '';
    this.previewRecipientFilter = 'all';
    this.pageCount = Math.max(document.sourceFile.pageCount, 1);
    this.activePage = 1;
    this.selectedFieldId = null;
    this.resetStepTwoUiState();
    this.syncRoleColorRegistry();
    this.viewerRotation = 0;
    this.releasePreviewUrl();
    this.previewUrl = URL.createObjectURL(pdfBlob);
  }

  private loadPublicConfigSession(token: string): void {
    this.loadingDocumentForEdit = true;
    this.publicConfigInitializing = true;
    this.editLoadError = '';
    this.formError = '';
    this.publicConfigSaveMessage = '';
    this.publicConfigToastMessage = '';

    this.documentApi
      .getPublicDocumentConfigSession(token)
      .pipe(
        switchMap((session) =>
          forkJoin({
            session: of(session),
            pdfBlob: this.documentApi.getPublicDocumentConfigPdf(token),
          }),
        ),
        finalize(() => {
          this.loadingDocumentForEdit = false;
          this.publicConfigInitializing = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ session, pdfBlob }) => {
          this.feedback.clearError();
          this.hydrateFromPublicConfigSession(session, pdfBlob);
          this.editLoadError = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load public document configuration session',
          });
          this.editLoadError = normalized.message;
          this.formError = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  private hydrateFromPublicConfigSession(session: PublicDocumentConfigSessionResponse, pdfBlob: Blob): void {
    this.publicConfigDocumentId = session.document.id;
    this.publicConfigCanEditFields = session.document.canEditFields;
    this.editingDocumentStatus = session.document.status;
    this.sourceType = session.document.sourceType;
    this.selectedTemplateId = '';
    this.selectedTemplate = null;
    this.selectedFile = new File([pdfBlob], session.document.sourceFile.originalName, {
      type: 'application/pdf',
      lastModified: Date.now(),
    });
    this.uploadedSourceFile = {
      fileId: session.document.sourceFile.fileId,
      s3Key: '',
      bucket: '',
      originalName: session.document.sourceFile.originalName,
      sizeBytes: pdfBlob.size,
      pageCount: session.document.sourceFile.pageCount,
      uploadedAt: new Date().toISOString(),
    };
    this.sourceTemplateUsageSettings = null;
    this.sourceFileReplaced = false;
    this.signingOrderEnabled = false;
    this.cc = session.document.ccEmails.join(', ');
    this.tags = session.document.tags.join(', ');
    this.documentTitle = session.document.title;
    this.documentMessage = session.document.message ?? '';
    this.documentSettings = this.normalizedDocumentSettings(session.document.documentSettings);
    this.recipients = session.recipients.map((recipient, index) => ({
      id: recipient.id,
      roleId: recipient.roleId,
      roleName: recipient.roleName,
      name: recipient.name,
      email: recipient.email,
      order: index + 1,
      roleLocked: true,
    }));
    this.nextGeneratedRecipientRoleIndex = this.recipients.length + 1;
    this.fields = this.dedupeFieldsByIdKeepLast(
      session.fields.map((field) => ({
        ...field,
        page: Math.max(1, Math.trunc(field.page || 1)),
        rect: { ...field.rect },
        fieldKey: trimOrUndefined(field.fieldKey) ?? field.fieldKey,
        label: trimOrUndefined(field.label),
        placeholder: trimOrUndefined(field.placeholder),
        settings: normalizeFieldSettingsForType(field.type, field.settings),
      })),
    );
    this.normalizeChoiceGroupMetadata();
    this.stepOneSubmitted = false;
    this.stepTwoSubmitted = false;
    this.currentStep = 2;
    this.stepTwoMode = session.document.canEditFields ? 'configure' : 'preview';
    this.activeRecipientRoleId = this.recipients[0]?.roleId ?? '';
    this.previewRecipientFilter = 'all';
    this.pageCount = Math.max(session.document.sourceFile.pageCount, 1);
    this.activePage = 1;
    this.selectedFieldId = this.fields[0]?.id ?? null;
    this.resetStepTwoUiState();
    this.stepTwoMode = session.document.canEditFields ? 'configure' : 'preview';
    this.syncRoleColorRegistry();
    this.viewerRotation = 0;
    this.releasePreviewUrl();
    this.previewUrl = URL.createObjectURL(pdfBlob);
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
  }

  addRecipient(): void {
    if (!this.canManageRecipients) {
      return;
    }

    this.recipients = [...this.recipients, this.createGeneratedRecipient()];
    this.reindexRecipients();
    this.syncRoleColorRegistry();
    this.cdr.markForCheck();
  }

  addRole(): void {
    this.addRecipient();
  }

  removeRole(roleId: string): void {
    if (!this.canManageRecipients) {
      return;
    }

    if (this.recipients.length === 1) {
      this.nextGeneratedRecipientRoleIndex = 1;
      this.recipients = [this.createGeneratedRecipient()];
      this.fields = [];
      this.syncRoleColorRegistry();
      this.resetStepTwoUiState();
      this.cdr.markForCheck();
      return;
    }

    this.recipients = this.recipients.filter((recipient) => recipient.id !== roleId);
    this.reindexRecipients();
    this.fields = this.fields.filter((field) => field.role !== roleId);
    if (this.selectedFieldId && !this.fields.some((field) => field.id === this.selectedFieldId)) {
      this.selectedFieldId = null;
    }

    if (!this.recipients.some((recipient) => recipient.roleId === this.activeRecipientRoleId)) {
      this.activeRecipientRoleId = this.recipients[0]?.roleId ?? '';
    }

    this.syncRoleColorRegistry();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  removeRecipient(recipientId: string): void {
    this.removeRole(recipientId);
  }

  dropRole(event: CdkDragDrop<RecipientDraft[]>): void {
    if (!this.canManageRecipients || !this.signingOrderEnabled || event.previousIndex === event.currentIndex) {
      return;
    }

    const reorderedRoles = [...this.recipients];
    moveItemInArray(reorderedRoles, event.previousIndex, event.currentIndex);
    this.recipients = reorderedRoles;
    this.reindexRecipients();
    this.syncRoleColorRegistry();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  hasRecipientFieldError(recipient: RecipientDraft, field: 'name' | 'email'): boolean {
    if (!this.stepOneSubmitted) {
      return false;
    }

    return !recipient[field].trim();
  }

  hasRecipientValidationError(recipient: RecipientDraft): boolean {
    return this.hasRecipientFieldError(recipient, 'name') || this.recipientEmailError(recipient).length > 0;
  }

  recipientEmailError(recipient: RecipientDraft): string {
    if (!this.stepOneSubmitted) {
      return '';
    }

    const email = recipient.email.trim();
    if (!email) {
      return 'Recipient email is required.';
    }

    if (!this.isValidEmail(email)) {
      return 'Valid recipient email is required.';
    }

    return '';
  }

  recipientDisplayLabel(recipient: RecipientDraft): string {
    const name = recipient.name.trim();
    const roleName = recipient.roleName.trim();
    if (name && roleName) {
      return `${name} (${roleName})`;
    }

    if (name) {
      return name;
    }

    if (roleName) {
      return roleName;
    }

    return recipient.id;
  }

  roleChipColor(roleId: string): string {
    this.syncRoleColorRegistry();
    return this.roleColorRegistry.get(roleId) ?? ROLE_COLOR_PALETTE[0];
  }

  fieldsCountForRole(roleId: string): number {
    return countFieldsForActor(this.fields.map((field) => this.toConfiguredField(field)), roleId);
  }

  visibleFieldsForSummary(): DocumentFieldDraft[] {
    return filterFieldsForSummary(this.fields.map((field) => this.toConfiguredField(field)), this.previewRecipientFilter);
  }

  fieldLabel(type: DocumentFieldType): string {
    return FIELD_LABELS[type];
  }

  setStepTwoMode(mode: 'configure' | 'preview'): void {
    this.clearFieldInteractionState();
    this.closeScheduleUi();
    this.stepTwoMode = mode;
    this.dragFieldType = null;
    if (mode === 'preview') {
      this.previewRecipientFilter = 'all';
    }
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPreviewRecipientFilterChanged(): void {
    this.scheduleOverlaySync();
  }

  handleFieldConfiguratorPreviewMetaChange(change: Partial<FieldConfiguratorPreviewMeta>): void {
    if (typeof change.title === 'string') {
      this.documentTitle = change.title;
    }
    if (typeof change.message === 'string' && this.canEditDocumentMessage) {
      this.documentMessage = change.message;
    }
    this.cdr.markForCheck();
  }

  handleFieldConfiguratorSettingsAction(action: FieldConfiguratorSettingAction): void {
    switch (action.type) {
      case 'update-role':
        this.updateSelectedFieldRole(action.payload);
        break;
      case 'update-required':
        this.updateSelectedFieldRequired(action.payload);
        break;
      case 'update-sender-action':
        this.updateSelectedFieldSenderAction(action.payload.key, action.payload.value);
        break;
      case 'update-key':
        this.updateSelectedFieldKey(action.payload);
        break;
      case 'update-textbox':
        this.updateSelectedFieldTextboxPatch(action.payload);
        break;
      case 'update-dropdown':
        this.updateSelectedFieldDropdownPatch(action.payload);
        break;
      case 'move-dropdown-option':
        this.moveSelectedDropdownOption(action.payload.index, action.payload.direction);
        break;
      case 'update-dropdown-option':
        this.updateSelectedDropdownOption(action.payload.index, action.payload.value);
        break;
      case 'remove-dropdown-option':
        this.removeSelectedDropdownOption(action.payload);
        break;
      case 'add-dropdown-option':
        this.addSelectedDropdownOption();
        break;
      case 'update-checkbox':
        this.updateSelectedFieldCheckboxPatch(action.payload);
        break;
      case 'toggle-checkbox-default':
        this.toggleSelectedCheckboxDefault(action.payload.option, action.payload.checked);
        break;
      case 'update-checkbox-value':
        this.updateSelectedCheckboxValue(action.payload.index, action.payload.value);
        break;
      case 'remove-checkbox-value':
        this.removeSelectedCheckboxValue(action.payload);
        break;
      case 'add-checkbox-value':
        this.addSelectedCheckboxValue();
        break;
      case 'update-radio':
        this.updateSelectedFieldRadioPatch(action.payload);
        break;
      case 'toggle-radio-default':
        this.toggleSelectedRadioDefault(action.payload.option, action.payload.checked);
        break;
      case 'update-radio-value':
        this.updateSelectedRadioValue(action.payload.index, action.payload.value);
        break;
      case 'remove-radio-value':
        this.removeSelectedRadioValue(action.payload);
        break;
      case 'add-radio-value':
        this.addSelectedRadioValue();
        break;
      case 'update-date':
        this.updateSelectedFieldDatePatch(action.payload);
        break;
      case 'update-image':
        this.updateSelectedFieldImagePatch(action.payload);
        break;
      case 'update-data-sync-tag':
        this.updateSelectedFieldDataSyncTag(action.payload);
        break;
      case 'update-position':
        this.updateSelectedFieldPosition(action.payload.axis, action.payload.value);
        break;
      case 'update-tab-index-mode':
        this.updateSelectedFieldTabIndexMode(action.payload);
        break;
      case 'update-tab-index':
        this.updateSelectedFieldTabIndex(action.payload);
        break;
      case 'update-font':
        this.updateSelectedFieldFontPatch(action.payload);
        break;
      case 'update-auto-positioning-enabled':
        this.updateSelectedFieldAutoPositioningEnabled(action.payload);
        break;
    }
  }

  onPagesLoaded(event: unknown): void {
    const pagesCount = this.extractPagesCount(event);
    if (pagesCount > 0) {
      this.pageCount = pagesCount;
    }

    this.activePage = this.clamp(Math.trunc(this.activePage || 1), 1, Math.max(this.pageCount, 1));
    this.finalizePdfLoaded();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPageRendered(): void {
    this.finalizePdfLoaded();
    this.ensureViewerRotationReset();
    this.scheduleOverlaySync();
  }

  onPageChanged(event: unknown): void {
    const page = this.extractPageChange(event);
    if (page <= 0) {
      return;
    }

    this.activePage = this.clamp(Math.trunc(page), 1, Math.max(this.pageCount, 1));
    this.scheduleOverlaySync();
  }

  onPdfLoadingStarts(): void {
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.cdr.markForCheck();
  }

  onPdfLoaded(): void {
    this.finalizePdfLoaded();
    this.ensureViewerRotationReset();
  }

  onViewerRotationChange(rotation: 0 | 90 | 180 | 270): void {
    this.viewerRotation = rotation;
    this.ensureViewerRotationReset();
  }

  onPdfLoadingFailed(_error: unknown): void {
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.dragFieldType = null;
    this.clearDropHoverClasses();

    const errorMessage = 'Invalid PDF file. Please upload a valid PDF document.';
    this.toast.error(errorMessage);
    this.formError = errorMessage;

    // In demo / fallback mode, if previewUrl is invalid, load the fallback demo document so the editor doesn't crash or display a broken screen
    if (this.previewUrl && this.previewUrl !== FALLBACK_BLANK_PDF) {
      this.pdfLoadError = '';
      this.previewUrl = FALLBACK_BLANK_PDF;
      this.pdfLoading = true;
      this.cdr.markForCheck();
      return;
    }

    this.pdfLoadError = '';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPaletteDragStart(event: DragEvent, type: DocumentFieldType): void {
    if (!this.canPlaceFields) {
      event.preventDefault();
      return;
    }

    this.dragFieldType = type;

    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', type);
      event.dataTransfer.effectAllowed = 'copy';
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPaletteDragEnd(): void {
    this.dragFieldType = null;
    this.clearDropHoverClasses();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPaletteTileClick(type: DocumentFieldType): void {
    if (!this.canPlaceFields) {
      return;
    }

    this.addField(type);
  }

  addField(type: DocumentFieldType = 'textbox'): void {
    if (!this.canMutateFieldStructure()) {
      return;
    }

    const roleId = this.activeRecipientRoleId || this.recipients[0]?.roleId;
    if (!roleId) {
      this.formError = 'Add a recipient before adding fields.';
      this.cdr.markForCheck();
      return;
    }

    const page = this.clamp(Math.trunc(this.activePage || 1), 1, Math.max(this.pageCount, 1));
    const pageElement = this.resolvePageElement(page);
    const measuredRect = this.computeFitRectForNewField(type, roleId, page, pageElement);
    if (!measuredRect) {
      this.formError = 'Please wait for page rendering to finish, then add the field again.';
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }

    const seedRect = this.clampRectToPage(measuredRect);
    if (this.wouldOverlapField(null, page, seedRect)) {
      this.formError = "Can't place field on top of another field.";
      this.cdr.markForCheck();
      return;
    }

    const field = this.createField(type, roleId, page, seedRect);
    this.fields = [...this.fields, field];
    this.selectedFieldId = field.id;
    this.formError = '';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  removeField(fieldId: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    if (this.interactingFieldId === fieldId) {
      this.clearFieldInteractionState();
    }

    this.fields = this.fields.filter((field) => field.id !== fieldId);
    if (this.selectedFieldId === fieldId) {
      this.selectedFieldId = null;
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  removeSelectedField(): void {
    if (!this.selectedFieldId) {
      return;
    }

    this.removeField(this.selectedFieldId);
  }

  selectField(fieldId: string): void {
    if (!this.fields.some((field) => field.id === fieldId)) {
      return;
    }

    this.selectedFieldId = fieldId;
    this.ensureSelectedChoiceGroupConsistency();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  clearSelectedField(): void {
    this.selectedFieldId = null;
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldRole(roleId: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }
    const groupIds = this.choiceGroupFieldIds(selected);
    if (groupIds.length <= 1) {
      this.updateSelectedField({ role: roleId });
      return;
    }
    this.fields = this.fields.map((field) => (groupIds.includes(field.id) ? { ...field, role: roleId } : field));
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldRequired(required: boolean): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }
    const groupIds = this.choiceGroupFieldIds(selected);
    if (groupIds.length <= 1) {
      this.updateSelectedField({ required });
      return;
    }
    this.fields = this.fields.map((field) => (groupIds.includes(field.id) ? { ...field, required } : field));
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldKey(fieldKey: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const sanitized = trimOrUndefined(fieldKey) ?? selected.fieldKey;
    this.updateSelectedField({ fieldKey: sanitized });
  }

  updateSelectedFieldDataSyncTag(dataSyncTag: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        dataSyncTag: trimOrUndefined(dataSyncTag),
      },
    }));
  }

  updateSelectedFieldTabIndexMode(mode: TemplateFieldTabIndexMode): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        tabIndexMode: mode,
        tabIndex: mode === 'custom' ? settings.common.tabIndex ?? 1 : undefined,
      },
    }));
  }

  updateSelectedFieldTabIndex(value: number): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        tabIndex: Math.max(1, Math.trunc(value || 1)),
      },
    }));
  }

  updateSelectedFieldSenderAction(
    key: 'allowEditing' | 'allowDeletion' | 'requireDefaultValue',
    value: boolean,
  ): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        senderActions: {
          ...settings.common.senderActions,
          [key]: value,
        },
      },
    }));
  }

  updateSelectedFieldAutoPositioningEnabled(value: boolean): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        autoPositioningEnabled: value,
      },
    }));
  }

  updateSelectedFieldFontPatch(patch: Partial<DocumentFieldSettings['font']>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      font: {
        ...settings.font,
        ...patch,
        ...(fieldSupportsCharacterSpacing(selected.type) ? {} : { characterSpacing: 0 }),
      },
    }));
  }

  updateSelectedFieldTextboxPatch(patch: Partial<TemplateTextboxSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      textbox: {
        ...settings.textbox,
        ...patch,
      } as TemplateTextboxSettings,
    }));
  }

  updateSelectedFieldDropdownPatch(patch: Partial<TemplateDropdownSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      dropdown: {
        ...settings.dropdown,
        ...patch,
      } as TemplateDropdownSettings,
    }));
  }

  updateSelectedFieldCheckboxPatch(patch: Partial<TemplateCheckboxSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    if (patch.optionGroupId !== undefined) {
      const selected = this.selectedField;
      if (!selected || selected.type !== 'checkbox') {
        return;
      }
      const values = this.checkboxGroupValuesModel();
      const sourceGroupId = this.resolveCheckboxGroupId(selected);
      const targetGroupId = trimOrUndefined(patch.optionGroupId) ?? sourceGroupId;
      this.syncSelectedCheckboxOptionFields(values, this.checkboxGroupDefaultValuesModel(), {
        sourceGroupId,
        targetGroupId,
      });
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      checkbox: {
        ...settings.checkbox,
        ...patch,
      } as TemplateCheckboxSettings,
    }));
    if (patch.size !== undefined) {
      const selected = this.selectedField;
      if (selected?.type === 'checkbox') {
        this.syncSelectedCheckboxOptionFields(this.checkboxGroupValuesModel(), this.checkboxGroupDefaultValuesModel());
      }
    }
  }

  updateSelectedFieldRadioPatch(patch: Partial<TemplateRadioSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    if (patch.groupName !== undefined) {
      const selected = this.selectedField;
      if (!selected || selected.type !== 'radio') {
        return;
      }
      const values = this.radioGroupValuesModel();
      const sourceGroupName = this.resolveRadioGroupName(selected);
      const targetGroupName = trimOrUndefined(patch.groupName) ?? sourceGroupName;
      this.syncSelectedRadioOptionFields(values, this.radioGroupDefaultValueModel(), {
        sourceGroupName,
        targetGroupName,
      });
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      radio: {
        ...settings.radio,
        ...patch,
      } as TemplateRadioSettings,
    }));
    if (patch.size !== undefined) {
      const selected = this.selectedField;
      if (selected?.type === 'radio') {
        this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), this.radioGroupDefaultValueModel());
      }
    }
  }

  updateSelectedFieldDatePatch(patch: Partial<TemplateDateSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      date: {
        ...settings.date,
        ...patch,
      } as TemplateDateSettings,
    }));
  }

  updateSelectedFieldImagePatch(patch: Partial<TemplateImageSettings>): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      image: {
        ...settings.image,
        ...patch,
      } as TemplateImageSettings,
    }));
  }

  addSelectedDropdownOption(): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const current = selected.settings.dropdown.options;
    this.updateSelectedFieldDropdownPatch({
      options: [...current, `Option${current.length + 1}`],
    });
  }

  removeSelectedDropdownOption(index: number): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...selected.settings.dropdown.options];
    if (index < 0 || index >= options.length) {
      return;
    }

    options.splice(index, 1);
    const normalized = sanitizeOptionValues(options, 'Option1');
    const defaultOption = selected.settings.dropdown.defaultOption;
    this.updateSelectedFieldDropdownPatch({
      options: normalized,
      defaultOption: defaultOption && normalized.includes(defaultOption) ? defaultOption : undefined,
    });
  }

  moveSelectedDropdownOption(index: number, direction: -1 | 1): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...selected.settings.dropdown.options];
    const nextIndex = index + direction;
    if (index < 0 || index >= options.length || nextIndex < 0 || nextIndex >= options.length) {
      return;
    }

    const temp = options[index];
    options[index] = options[nextIndex]!;
    options[nextIndex] = temp!;
    this.updateSelectedFieldDropdownPatch({ options });
  }

  updateSelectedDropdownOption(index: number, value: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...selected.settings.dropdown.options];
    if (index < 0 || index >= options.length) {
      return;
    }

    options[index] = value;
    this.updateSelectedFieldDropdownPatch({ options });
  }

  addSelectedCheckboxValue(): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = this.checkboxGroupValuesModel();
    this.syncSelectedCheckboxOptionFields([...current, `Checkbox${current.length + 1}`], this.checkboxGroupDefaultValuesModel());
  }

  removeSelectedCheckboxValue(index: number): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = [...this.checkboxGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const removed = current[index]!;
    current.splice(index, 1);
    const defaults = this.checkboxGroupDefaultValuesModel().filter((entry) => entry !== removed);
    this.syncSelectedCheckboxOptionFields(current, defaults);
  }

  updateSelectedCheckboxValue(index: number, value: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = [...this.checkboxGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const previous = current[index]!;
    current[index] = value;
    const normalized = sanitizeOptionValues(current, 'Checkbox1');
    const defaults = [...this.checkboxGroupDefaultValuesModel()].map((entry) =>
      entry === previous ? (normalized[index] ?? entry) : entry,
    );
    this.syncSelectedCheckboxOptionFields(normalized, defaults);
  }

  toggleSelectedCheckboxDefault(value: string, checked: boolean): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = this.checkboxGroupValuesModel();
    const defaults = new Set(this.checkboxGroupDefaultValuesModel());
    if (checked) {
      defaults.add(value);
    } else {
      defaults.delete(value);
    }
    this.syncSelectedCheckboxOptionFields(current, Array.from(defaults));
  }

  addSelectedRadioValue(): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = this.radioGroupValuesModel();
    this.syncSelectedRadioOptionFields([...current, `Radio${current.length + 1}`], this.radioGroupDefaultValueModel());
  }

  removeSelectedRadioValue(index: number): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = [...this.radioGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const removed = current[index]!;
    current.splice(index, 1);
    const currentDefault = this.radioGroupDefaultValueModel();
    const nextDefault = currentDefault === removed ? undefined : currentDefault;
    this.syncSelectedRadioOptionFields(current, nextDefault);
  }

  updateSelectedRadioValue(index: number, value: string): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = [...this.radioGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const previous = current[index]!;
    current[index] = value;
    const normalized = sanitizeOptionValues(current, 'Radio1');
    const currentDefault = this.radioGroupDefaultValueModel();
    const nextDefault = currentDefault === previous ? normalized[index] : currentDefault;
    this.syncSelectedRadioOptionFields(normalized, nextDefault);
  }

  toggleSelectedRadioDefault(value: string, checked: boolean): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    if (!checked) {
      this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), undefined);
      return;
    }
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), value);
  }

  checkboxGroupNameModel(): string {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return '';
    }
    return this.checkboxGroupSnapshot(selected).groupId;
  }

  checkboxGroupValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return [];
    }
    return this.checkboxGroupSnapshot(selected).values;
  }

  checkboxGroupDefaultValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return [];
    }
    return this.checkboxGroupSnapshot(selected).defaultValues;
  }

  radioGroupNameModel(): string {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return '';
    }
    return this.radioGroupSnapshot(selected).groupName;
  }

  radioGroupValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return [];
    }
    return this.radioGroupSnapshot(selected).values;
  }

  radioGroupDefaultValueModel(): string | undefined {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return undefined;
    }
    return this.radioGroupSnapshot(selected).defaultValue;
  }

  private ensureSelectedChoiceGroupConsistency(): void {
    const selected = this.selectedField;
    if (!selected || (selected.type !== 'checkbox' && selected.type !== 'radio')) {
      return;
    }

    this.ensureChoiceGroupConsistency(selected, selected.type);
  }

  private ensureChoiceGroupConsistency(anchor: ConfiguredDocumentField, type: 'checkbox' | 'radio'): void {
    if (type === 'checkbox') {
      const snapshot = this.checkboxGroupSnapshot(anchor);
      if (!this.checkboxGroupNeedsRepair(snapshot)) {
        return;
      }

      const baseSettings = normalizeFieldSettings(anchor.settings);
      this.applyChoiceGroupChanges(
        anchor,
        snapshot.fields,
        snapshot.values,
        (optionValue) => ({
          ...baseSettings,
          checkbox: {
            ...baseSettings.checkbox,
            optionGroupId: snapshot.groupId,
            values: snapshot.values,
            value: optionValue,
            checked: snapshot.defaultValues.includes(optionValue),
            defaultValues: snapshot.defaultValues,
          },
        }),
      );
      return;
    }

    const snapshot = this.radioGroupSnapshot(anchor);
    if (!this.radioGroupNeedsRepair(snapshot)) {
      return;
    }

    const baseSettings = normalizeFieldSettings(anchor.settings);
    this.applyChoiceGroupChanges(
      anchor,
      snapshot.fields,
      snapshot.values,
      (optionValue) => ({
        ...baseSettings,
        radio: {
          ...baseSettings.radio,
          groupName: snapshot.groupName,
          values: snapshot.values,
          value: optionValue,
          checked: snapshot.defaultValue === optionValue,
          defaultValue: snapshot.defaultValue,
        },
      }),
    );
  }

  private checkboxGroupSnapshot(
    anchor: ConfiguredDocumentField,
    preferredGroupId?: string,
  ): { groupId: string; fields: DocumentFieldDraft[]; values: string[]; defaultValues: string[] } {
    const context = this.groupContextForField(anchor, 'checkbox', preferredGroupId);
    const values = this.canonicalChoiceGroupValues(context.fields, 'checkbox');
    const defaults = new Set<string>();

    for (const memberRaw of context.fields) {
      const member = this.toConfiguredField(memberRaw);
      const settings = member.settings.checkbox;
      const optionValue = trimOrUndefined(settings.value);
      if (settings.checked && optionValue && values.includes(optionValue)) {
        defaults.add(optionValue);
      }
      for (const defaultValue of sanitizeOptionValues(settings.defaultValues ?? [], '')) {
        if (values.includes(defaultValue)) {
          defaults.add(defaultValue);
        }
      }
    }

    return {
      groupId: context.groupKey,
      fields: context.fields,
      values,
      defaultValues: values.filter((value) => defaults.has(value)),
    };
  }

  private radioGroupSnapshot(
    anchor: ConfiguredDocumentField,
    preferredGroupName?: string,
  ): { groupName: string; fields: DocumentFieldDraft[]; values: string[]; defaultValue: string | undefined } {
    const context = this.groupContextForField(anchor, 'radio', preferredGroupName);
    const values = this.canonicalChoiceGroupValues(context.fields, 'radio');

    let defaultValue = context.fields
      .map((member) => trimOrUndefined(this.toConfiguredField(member).settings.radio.defaultValue))
      .find((value): value is string => !!value && values.includes(value));

    if (!defaultValue) {
      const checkedField = context.fields.find((member) => this.toConfiguredField(member).settings.radio.checked);
      const checkedValue = checkedField ? this.choiceValue(this.toConfiguredField(checkedField), 'radio') : undefined;
      if (checkedValue && values.includes(checkedValue)) {
        defaultValue = checkedValue;
      }
    }

    return {
      groupName: context.groupKey,
      fields: context.fields,
      values,
      defaultValue,
    };
  }

  private canonicalChoiceGroupValues(fields: DocumentFieldDraft[], type: 'checkbox' | 'radio'): string[] {
    const fallback = type === 'checkbox' ? 'Checkbox1' : 'Radio1';
    const normalizedFields = fields.map((field) => this.toConfiguredField(field));
    const valueLists = normalizedFields.map((member) => this.choiceValues(member, type));
    let baseList = valueLists[0] ? [...valueLists[0]] : [fallback];

    for (const candidate of valueLists) {
      if (candidate.length > baseList.length) {
        baseList = [...candidate];
      }
    }

    const ordered: string[] = [];
    const pushUnique = (value: string | undefined): void => {
      const trimmed = trimOrUndefined(value);
      if (!trimmed || ordered.includes(trimmed)) {
        return;
      }
      ordered.push(trimmed);
    };

    for (const value of baseList) {
      pushUnique(value);
    }
    for (const member of normalizedFields) {
      pushUnique(this.choiceValue(member, type));
    }
    for (const values of valueLists) {
      for (const value of values) {
        pushUnique(value);
      }
    }

    return sanitizeOptionValues(ordered, fallback);
  }

  private checkboxGroupNeedsRepair(snapshot: {
    groupId: string;
    fields: DocumentFieldDraft[];
    values: string[];
    defaultValues: string[];
  }): boolean {
    if (snapshot.fields.length !== snapshot.values.length) {
      return true;
    }

    for (let index = 0; index < snapshot.values.length; index += 1) {
      const memberRaw = snapshot.fields[index];
      const expectedValue = snapshot.values[index]!;
      if (!memberRaw) {
        return true;
      }

      const member = this.toConfiguredField(memberRaw);
      const settings = member.settings.checkbox;
      const groupId = trimOrUndefined(settings.optionGroupId);
      const value = trimOrUndefined(settings.value);
      const defaults = sanitizeOptionValues(settings.defaultValues ?? [], '').filter((entry) => snapshot.values.includes(entry));
      const expectedChecked = snapshot.defaultValues.includes(expectedValue);

      if (groupId !== snapshot.groupId) {
        return true;
      }
      if (!this.areStringArraysEqual(settings.values ?? [], snapshot.values)) {
        return true;
      }
      if (value !== expectedValue) {
        return true;
      }
      if (!this.areStringArraysEqual(defaults, snapshot.defaultValues)) {
        return true;
      }
      if (settings.checked !== expectedChecked) {
        return true;
      }
    }

    return false;
  }

  private radioGroupNeedsRepair(snapshot: {
    groupName: string;
    fields: DocumentFieldDraft[];
    values: string[];
    defaultValue: string | undefined;
  }): boolean {
    if (snapshot.fields.length !== snapshot.values.length) {
      return true;
    }

    for (let index = 0; index < snapshot.values.length; index += 1) {
      const memberRaw = snapshot.fields[index];
      const expectedValue = snapshot.values[index]!;
      if (!memberRaw) {
        return true;
      }

      const member = this.toConfiguredField(memberRaw);
      const settings = member.settings.radio;
      const groupName = trimOrUndefined(settings.groupName);
      const value = trimOrUndefined(settings.value);
      const defaultValue = trimOrUndefined(settings.defaultValue);
      const expectedChecked = snapshot.defaultValue === expectedValue;

      if (groupName !== snapshot.groupName) {
        return true;
      }
      if (!this.areStringArraysEqual(settings.values ?? [], snapshot.values)) {
        return true;
      }
      if (value !== expectedValue) {
        return true;
      }
      if (defaultValue !== snapshot.defaultValue) {
        return true;
      }
      if (settings.checked !== expectedChecked) {
        return true;
      }
    }

    return false;
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) {
        return false;
      }
    }
    return true;
  }

  private syncSelectedCheckboxOptionFields(
    valuesInput: string[],
    defaultsInput: string[],
    groupOverride?: { sourceGroupId?: string; targetGroupId?: string },
  ): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const values = sanitizeOptionValues(valuesInput, 'Checkbox1');
    const defaults = new Set(sanitizeOptionValues(defaultsInput, ''));
    const context = this.groupContextForField(selected, 'checkbox', groupOverride?.sourceGroupId);
    const sourceGroupId = context.groupKey;
    const groupId = groupOverride?.targetGroupId ?? sourceGroupId;
    const groupFields = context.fields;
    this.applyChoiceGroupChanges(
      selected,
      groupFields,
      values,
      (optionValue) => {
        const checked = defaults.has(optionValue);
        return {
          ...normalizeFieldSettings(selected.settings),
          checkbox: {
            ...normalizeFieldSettings(selected.settings).checkbox,
            optionGroupId: groupId,
            values,
            value: optionValue,
            checked,
            defaultValues: values.filter((entry) => defaults.has(entry)),
          },
        };
      },
    );
  }

  private syncSelectedRadioOptionFields(
    valuesInput: string[],
    defaultValueInput?: string,
    groupOverride?: { sourceGroupName?: string; targetGroupName?: string },
  ): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const values = sanitizeOptionValues(valuesInput, 'Radio1');
    const context = this.groupContextForField(selected, 'radio', groupOverride?.sourceGroupName);
    const sourceGroupName = context.groupKey;
    const groupName = groupOverride?.targetGroupName ?? sourceGroupName;
    const normalizedDefault = trimOrUndefined(defaultValueInput);
    const defaultValue = normalizedDefault && values.includes(normalizedDefault) ? normalizedDefault : undefined;
    const groupFields = context.fields;
    this.applyChoiceGroupChanges(
      selected,
      groupFields,
      values,
      (optionValue) => ({
        ...normalizeFieldSettings(selected.settings),
        radio: {
          ...normalizeFieldSettings(selected.settings).radio,
          groupName,
          values,
          value: optionValue,
          checked: defaultValue === optionValue,
          defaultValue,
        },
      }),
    );
  }

  private checkboxGroupFields(anchor: ConfiguredDocumentField, groupId?: string): DocumentFieldDraft[] {
    return this.groupContextForField(anchor, 'checkbox', groupId).fields;
  }

  private resolveCheckboxGroupId(anchor: ConfiguredDocumentField): string {
    return this.groupContextForField(anchor, 'checkbox').groupKey;
  }

  private resolveRadioGroupName(anchor: ConfiguredDocumentField): string {
    return this.groupContextForField(anchor, 'radio').groupKey;
  }

  private radioGroupFields(anchor: ConfiguredDocumentField, groupName?: string): DocumentFieldDraft[] {
    return this.groupContextForField(anchor, 'radio', groupName).fields;
  }

  private groupContextForField(
    anchor: ConfiguredDocumentField,
    type: 'checkbox' | 'radio',
    preferredGroupKey?: string,
  ): { groupKey: string; fields: DocumentFieldDraft[] } {
    const candidates = this.choiceFieldsOnPageForRole(anchor, type);
    const groupKey = trimOrUndefined(preferredGroupKey) || this.resolveGroupKey(anchor, type, candidates);
    const anchorExplicitGroupKey = this.choiceExplicitGroupKey(anchor, type);
    const anchorHasStableGroupKey = !!anchorExplicitGroupKey && !this.isLegacyAutoGroupKey(anchorExplicitGroupKey);
    const grouped = new Map<string, DocumentFieldDraft>();

    if (anchorHasStableGroupKey) {
      for (const field of candidates) {
        const explicit = this.choiceExplicitGroupKey(field, type);
        if (explicit === anchorExplicitGroupKey) {
          grouped.set(field.id, field);
        }
      }
    }

    for (const field of candidates) {
      const explicit = this.choiceExplicitGroupKey(field, type);
      if (explicit === groupKey) {
        grouped.set(field.id, field);
      }
    }

    for (const field of candidates) {
      const explicit = this.choiceExplicitGroupKey(field, type);
      if ((!explicit || this.isLegacyAutoGroupKey(explicit)) && this.hasSharedChoiceProfile(anchor, field, type)) {
        grouped.set(field.id, field);
      }
    }

    grouped.set(anchor.id, anchor);

    return {
      groupKey,
      fields: [...grouped.values()].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y)),
    };
  }

  private resolveGroupKey(
    anchor: ConfiguredDocumentField,
    type: 'checkbox' | 'radio',
    candidates: ConfiguredDocumentField[],
  ): string {
    const explicit = this.choiceExplicitGroupKey(anchor, type);
    if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
      return explicit;
    }

    const siblingKey = candidates
      .filter((field) => field.id !== anchor.id)
      .filter((field) => this.hasSharedChoiceProfile(anchor, field, type))
      .map((field) => this.choiceExplicitGroupKey(field, type))
      .find((value) => !!value && !this.isLegacyAutoGroupKey(value));

    if (siblingKey) {
      return siblingKey;
    }

    const anchorFieldKey = trimOrUndefined(anchor.fieldKey);
    if (anchorFieldKey) {
      return anchorFieldKey;
    }

    return this.choiceFallbackGroupKey(anchor, type, candidates);
  }

  private choiceFallbackGroupKey(
    anchor: ConfiguredDocumentField,
    type: 'checkbox' | 'radio',
    candidates: ConfiguredDocumentField[],
  ): string {
    const prefix = type === 'checkbox' ? 'CheckboxGroup' : 'RadioGroup';
    const ordered = [...candidates].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y));
    const index = ordered.findIndex((field) => field.id === anchor.id);
    if (index >= 0) {
      return `${prefix}${index + 1}`;
    }
    return `${prefix}1`;
  }

  private choiceFieldsOnPageForRole(
    anchor: ConfiguredDocumentField,
    type: 'checkbox' | 'radio',
  ): ConfiguredDocumentField[] {
    return this.fields
      .map((field) => this.toConfiguredField(field))
      .filter((field) => field.type === type && field.page === anchor.page && field.role === anchor.role);
  }

  private choiceExplicitGroupKey(field: ConfiguredDocumentField, type: 'checkbox' | 'radio'): string | undefined {
    if (type === 'checkbox') {
      return trimOrUndefined(field.settings.checkbox.optionGroupId);
    }
    return trimOrUndefined(field.settings.radio.groupName);
  }

  private choiceValues(field: ConfiguredDocumentField, type: 'checkbox' | 'radio'): string[] {
    if (type === 'checkbox') {
      return sanitizeOptionValues(field.settings.checkbox.values ?? [field.settings.checkbox.value ?? 'Checkbox1'], 'Checkbox1');
    }
    return sanitizeOptionValues(field.settings.radio.values ?? [field.settings.radio.value ?? 'Radio1'], 'Radio1');
  }

  private choiceValue(field: ConfiguredDocumentField, type: 'checkbox' | 'radio'): string | undefined {
    if (type === 'checkbox') {
      return trimOrUndefined(field.settings.checkbox.value);
    }
    return trimOrUndefined(field.settings.radio.value);
  }

  private hasSharedChoiceProfile(
    anchor: ConfiguredDocumentField,
    candidate: ConfiguredDocumentField,
    type: 'checkbox' | 'radio',
  ): boolean {
    if (anchor.id === candidate.id) {
      return true;
    }

    const anchorValues = this.choiceValues(anchor, type);
    const candidateValues = this.choiceValues(candidate, type);
    const anchorValue = this.choiceValue(anchor, type);
    const candidateValue = this.choiceValue(candidate, type);

    if (anchorValue && candidateValues.includes(anchorValue)) {
      return true;
    }
    if (candidateValue && anchorValues.includes(candidateValue)) {
      return true;
    }

    if (anchorValues.length === 0 || candidateValues.length === 0) {
      return false;
    }

    return anchorValues.join('\u0000') === candidateValues.join('\u0000');
  }

  private isLegacyAutoGroupKey(value: string | undefined): boolean {
    return !!value && LEGACY_AUTO_GROUP_KEY_PATTERN.test(value);
  }

  private applyChoiceGroupChanges(
    selected: ConfiguredDocumentField,
    existingGroup: DocumentFieldDraft[],
    values: string[],
    makeSettings: (value: string, index: number) => DocumentFieldSettings,
  ): void {
    const anchor = existingGroup.find((field) => field.id === selected.id) ?? selected;
    const group = existingGroup.length > 0 ? existingGroup : [anchor];
    const groupIds = new Set(group.map((field) => field.id));
    const nextFields = this.fields.filter((field) => !groupIds.has(field.id));
    const replacements: DocumentFieldDraft[] = [];
    for (let index = 0; index < values.length; index += 1) {
      const optionValue = values[index]!;
      const current = group[index];
      const settings = makeSettings(optionValue, index);
      if (current) {
        replacements.push({
          ...current,
          rect: this.choiceRectFromSettings(current.rect, selected.page, selected.type, settings),
          settings,
        });
        continue;
      }
      const seedRect = this.findChoiceSiblingRect(anchor, index);
      if (!seedRect) {
        continue;
      }
      const created = this.createField(selected.type, selected.role, selected.page, seedRect);
      replacements.push({
        ...created,
        rect: this.choiceRectFromSettings(seedRect, selected.page, selected.type, settings),
        required: selected.required,
        label: selected.label,
        placeholder: selected.placeholder,
        settings,
      });
    }

    this.fields = this.dedupeFieldsByIdKeepLast([...nextFields, ...replacements]);
    this.selectedFieldId = replacements.find((field) => field.id === selected.id)?.id ?? replacements[0]?.id ?? this.selectedFieldId;
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  private findChoiceSiblingRect(anchor: { page: number; rect: DocumentFieldDraft['rect'] }, index: number): DocumentFieldDraft['rect'] | null {
    const gap = 0.004;
    const baseRect = { ...anchor.rect };
    const startY = baseRect.y + index * (baseRect.height + gap);
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const candidate = this.clampRectToPage({ ...baseRect, y: startY + attempt * (baseRect.height + gap) });
      if (!this.wouldOverlapField(null, anchor.page, candidate)) {
        return candidate;
      }
    }
    return null;
  }

  private choiceRectFromSettings(
    rect: DocumentFieldDraft['rect'],
    page: number,
    type: DocumentFieldType,
    settings: DocumentFieldSettings,
  ): DocumentFieldDraft['rect'] {
    if (type !== 'checkbox' && type !== 'radio') {
      return rect;
    }
    const pageElement = this.resolvePageElement(page);
    const pageRect = pageElement?.getBoundingClientRect();
    if (!pageRect || pageRect.width <= 0 || pageRect.height <= 0) {
      return rect;
    }
    const sizePx = type === 'checkbox' ? settings.checkbox.size : settings.radio.size;
    if (!Number.isFinite(sizePx) || sizePx <= 0) {
      return rect;
    }
    const width = this.clamp(sizePx / pageRect.width, MIN_MEASURED_FIELD_WIDTH, MAX_MEASURED_FIELD_WIDTH);
    const height = this.clamp(sizePx / pageRect.height, MIN_MEASURED_FIELD_HEIGHT, MAX_MEASURED_FIELD_HEIGHT);
    return this.clampRectToPage({ ...rect, width, height });
  }

  updateSelectedFieldPosition(axis: 'x' | 'y', value: number): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const pageElement = this.viewerHost?.nativeElement.querySelector<HTMLElement>(
      `.page[data-page-number="${selected.page}"]`,
    );
    if (!pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const size = axis === 'x' ? pageRect.width : pageRect.height;
    const normalized = this.clamp((value || 0) / size, 0, 1 - (axis === 'x' ? selected.rect.width : selected.rect.height));
    const rect = {
      ...selected.rect,
      [axis]: normalized,
    };

    this.patchField(selected.id, { rect });
    this.cdr.markForCheck();
  }

  selectedFieldPositionPx(axis: 'x' | 'y'): number {
    const selected = this.selectedField;
    if (!selected) {
      return 0;
    }

    const pageElement = this.viewerHost?.nativeElement.querySelector<HTMLElement>(
      `.page[data-page-number="${selected.page}"]`,
    );
    if (!pageElement) {
      return 0;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const size = axis === 'x' ? pageRect.width : pageRect.height;
    const normalized = axis === 'x' ? selected.rect.x : selected.rect.y;
    return Math.round(normalized * size);
  }

  goToStepTwo(): void {
    this.stepOneSubmitted = true;
    if (this.isStepOneInvalid()) {
      this.cdr.markForCheck();
      return;
    }

    this.formError = '';
    this.currentStep = 2;
    this.stepTwoMode = 'configure';
    this.stepTwoSubmitted = false;
    this.selectedFieldId = this.fields[0]?.id ?? null;
    this.activeRecipientRoleId = this.recipients[0]?.roleId ?? '';
    this.previewRecipientFilter = 'all';
    this.pageCount = Math.max(this.uploadedSourceFile?.pageCount ?? this.selectedTemplate?.sourceFile.pageCount ?? 1, 1);
    this.activePage = 1;
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.viewerMounted = this.viewerPdfSource !== null;
    this.dragFieldType = null;
    this.fieldRectsNormalizedForSession = false;
    this.clearFieldInteractionState();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  goToStepOne(): void {
    this.clearFieldInteractionState();
    this.currentStep = 1;
    this.stepTwoMode = 'configure';
    this.closeScheduleUi();
    this.dragFieldType = null;
    this.viewerMounted = false;
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.cdr.markForCheck();
  }

  sendNow(): void {
    if (!this.showSendAction) {
      return;
    }

    this.closeScheduleUi();
    this.send(undefined);
  }

  toggleSendOptions(): void {
    if (this.isSendActionDisabled) {
      return;
    }

    if (this.sendOptionsOpen) {
      this.closeSendOptions();
      this.cdr.markForCheck();
      return;
    }

    this.closeScheduleUi();
    this.sendOptionsOpen = true;
    this.cdr.markForCheck();
  }

  openSchedulePickerFromSendOptions(): void {
    if (this.isSendActionDisabled) {
      return;
    }

    this.openSchedulePicker();
  }

  toggleSchedulePicker(): void {
    if (this.isSendActionDisabled) {
      return;
    }

    if (this.schedulePickerOpen) {
      this.closeSchedulePicker();
      return;
    }

    this.openSchedulePicker();
  }

  closeSendOptions(): void {
    this.sendOptionsOpen = false;
  }

  private openSchedulePicker(): void {
    this.closeScheduleUi();
    this.schedulePickerOpen = true;
    this.cdr.markForCheck();
  }

  closeSchedulePicker(): void {
    if (!this.schedulePickerOpen) {
      return;
    }

    this.closeScheduleUi();
    this.cdr.markForCheck();
  }

  chooseQuickSchedule(slot: ScheduleQuickSlot): void {
    const validationError = this.validateScheduleAtLocal(slot.atLocal);
    if (validationError) {
      this.formError = validationError;
      this.cdr.markForCheck();
      return;
    }

    this.pendingScheduleAtLocal = slot.atLocal;
    this.schedulePickerOpen = false;
    this.scheduleConfirmModalOpen = true;
    this.formError = '';
    this.cdr.markForCheck();
  }

  openScheduleDateTimeModal(): void {
    const initialLocal = this.pendingScheduleAtLocal || this.scheduleQuickSlots[0]?.atLocal || '';
    if (initialLocal) {
      const [dateValue = '', timeValue = ''] = initialLocal.split('T');
      this.scheduleDateInput = dateValue;
      this.scheduleTimeInput = timeValue.slice(0, 5);
    }

    this.schedulePickerOpen = false;
    this.scheduleDateTimeModalOpen = true;
    this.cdr.markForCheck();
  }

  closeScheduleDateTimeModal(): void {
    if (!this.scheduleDateTimeModalOpen) {
      return;
    }

    this.closeScheduleUi();
    this.cdr.markForCheck();
  }

  backToSchedulePicker(): void {
    this.scheduleDateTimeModalOpen = false;
    this.schedulePickerOpen = true;
    this.cdr.markForCheck();
  }

  continueFromScheduleDateTime(): void {
    const localDateTime = this.composeLocalDateTime(this.scheduleDateInput, this.scheduleTimeInput);
    if (!localDateTime) {
      this.formError = SCHEDULE_REQUIRED_ERROR;
      this.cdr.markForCheck();
      return;
    }

    const validationError = this.validateScheduleAtLocal(localDateTime);
    if (validationError) {
      this.formError = validationError;
      this.cdr.markForCheck();
      return;
    }

    this.pendingScheduleAtLocal = localDateTime;
    this.scheduleDateTimeModalOpen = false;
    this.scheduleConfirmModalOpen = true;
    this.formError = '';
    this.cdr.markForCheck();
  }

  closeScheduleConfirmModal(): void {
    if (!this.scheduleConfirmModalOpen) {
      return;
    }

    this.closeScheduleUi();
    this.cdr.markForCheck();
  }

  submitScheduledSend(): void {
    if (!this.showSendAction) {
      return;
    }

    if (!this.pendingScheduleAtLocal) {
      this.formError = SCHEDULE_REQUIRED_ERROR;
      this.cdr.markForCheck();
      return;
    }

    const validationError = this.validateScheduleAtLocal(this.pendingScheduleAtLocal);
    if (validationError) {
      this.formError = validationError;
      this.cdr.markForCheck();
      return;
    }

    const scheduleAtLocal = this.pendingScheduleAtLocal;
    this.closeScheduleUi();
    this.send(scheduleAtLocal);
  }

  scheduledAtDisplay(): string {
    if (!this.pendingScheduleAtLocal) {
      return '';
    }

    const parsed = this.parseLocalDateTime(this.pendingScheduleAtLocal);
    if (!parsed) {
      return '';
    }

    return this.formatScheduleSummaryTime(parsed);
  }

  scheduleSummaryFileName(): string {
    return this.selectedFile?.name || this.uploadedSourceFile?.originalName || this.selectedTemplate?.sourceFile.originalName || '-';
  }

  saveChanges(): void {
    if (this.savingChanges) {
      return;
    }

    if (this.currentStep === 1) {
      if (!this.isEditMode) {
        return;
      }

      this.stepOneSubmitted = true;
      if (this.isStepOneInvalid()) {
        this.cdr.markForCheck();
        return;
      }
    } else {
      this.stepTwoSubmitted = true;
      if (this.isStepTwoInvalid()) {
        this.cdr.markForCheck();
        return;
      }
    }

    if (this.currentStep !== 1 && this.currentStep !== 2) {
      this.cdr.markForCheck();
      return;
    }

    const isEditingExistingDocument = this.isEditMode;
    const sourceFile$ = isEditingExistingDocument ? this.resolveSourceFileForSave() : this.resolveSourceFileForPayload();
    if (!sourceFile$) {
      this.formError = 'A source file is required.';
      this.cdr.markForCheck();
      return;
    }

    this.savingChanges = true;
    this.formError = '';
    this.publicConfigSaveMessage = '';
    this.publicConfigToastMessage = '';

    if (this.isPublicConfigureMode && this.publicConfigToken) {
      this.documentApi
        .savePublicDocumentConfigFields(this.publicConfigToken, {
          fields: this.buildFieldsPayload(),
        })
        .pipe(
          finalize(() => {
            this.savingChanges = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (response) => {
            this.feedback.clearError();
            this.publicConfigDocumentId = response.documentId;
            this.editingDocumentStatus = response.status;
            this.fields = this.dedupeFieldsByIdKeepLast(
              response.fields.map((field) => ({
                ...field,
                settings: normalizeFieldSettingsForType(field.type, field.settings),
              })),
            );
            this.publicConfigSaveMessage = 'Fields saved successfully.';
            this.publicConfigToastMessage = '';
            this.notifyParentDocumentConfigSaved(response.documentId, response.status);
          },
          error: (error: unknown) => {
            const normalized = normalizeApiError(error, {
              fallbackMessage: 'Failed to save document fields',
            });
            this.formError = normalized.message;
            this.feedback.showError({ ...normalized });
            this.cdr.markForCheck();
          },
        });
      return;
    }

    sourceFile$
      .pipe(
        switchMap((sourceFile) => {
          if (isEditingExistingDocument && this.editingDocumentId) {
            return this.documentApi.updateDocument(this.editingDocumentId, this.buildPayload(sourceFile, undefined));
          }

          return this.documentApi.createDocument(this.buildPayload(sourceFile, undefined));
        }),
        finalize(() => {
          this.savingChanges = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (document) => {
          this.feedback.clearError();
          if (isEditingExistingDocument) {
            void this.router.navigate(['/documents']);
            return;
          }

          this.activateEditModeFromSavedDraft(document);
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to save document changes',
          });
          this.formError = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  private activateEditModeFromSavedDraft(document: DocumentSummary): void {
    this.editingDocumentId = document._id;
    this.editingDocumentStatus = document.status;
    this.editingSourceTemplateId = document.sourceTemplateId ?? document.templateId ?? null;
    this.sourceType = document.sourceType;
    this.selectedTemplateId = this.editingSourceTemplateId ?? '';
    this.uploadedSourceFile = { ...document.sourceFile };
    this.sourceTemplateUsageSettings = document.sourceType === 'template'
      ? { ...(document.sourceTemplateUsageSettings ?? DEFAULT_TEMPLATE_USAGE_SETTINGS) }
      : null;
    this.sourceFileReplaced = false;
    this.formError = '';
    this.cdr.markForCheck();
  }

  private send(scheduleAtLocal: string | undefined): void {
    if (this.sending) {
      return;
    }

    this.stepTwoSubmitted = true;

    if (this.isStepTwoInvalid()) {
      this.cdr.markForCheck();
      return;
    }

    const sourceFile$ = this.canSendEditedDraft ? this.resolveSourceFileForSave() : this.resolveSourceFileForPayload();
    if (!sourceFile$) {
      this.formError = 'A source file is required.';
      this.cdr.markForCheck();
      return;
    }

    this.sending = true;
    this.formError = '';
    this.publicConfigSaveMessage = '';
    this.publicConfigToastMessage = '';

    if (this.isPublicConfigureMode && this.publicConfigToken) {
      this.documentApi
        .sendPublicDocumentConfig(this.publicConfigToken, {
          fields: this.buildFieldsPayload(),
          sendAt: this.toIso(scheduleAtLocal),
        })
        .pipe(
          finalize(() => {
            this.sending = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (response) => {
            this.feedback.clearError();
            this.publicConfigDocumentId = response.document._id;
            this.editingDocumentStatus = response.document.status;
            this.publicConfigCanEditFields = false;
            this.stepTwoMode = 'preview';
            this.closeScheduleUi();
            this.publicConfigToastMessage = response.status === 'scheduled'
              ? 'Document scheduled successfully.'
              : 'Document sent successfully.';
          },
          error: (error: unknown) => {
            const normalized = normalizeApiError(error, {
              fallbackMessage: 'Failed to send document',
            });
            this.formError = normalized.message;
            this.feedback.showError({ ...normalized });
            this.cdr.markForCheck();
          },
        });

      return;
    }

    if (this.canSendEditedDraft && this.editingDocumentId) {
      sourceFile$
        .pipe(
          switchMap((sourceFile) =>
            this.documentApi.updateDocument(this.editingDocumentId!, this.buildPayload(sourceFile, scheduleAtLocal)),
          ),
          switchMap(() => this.documentApi.sendDocument(this.editingDocumentId!, this.toIso(scheduleAtLocal))),
          finalize(() => {
            this.sending = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: () => {
            this.feedback.clearError();
            void this.router.navigate(['/documents']);
          },
          error: (error: unknown) => {
            const normalized = normalizeApiError(error, {
              fallbackMessage: 'Failed to send document',
            });
            this.formError = normalized.message;
            this.feedback.showError({ ...normalized });
            this.cdr.markForCheck();
          },
        });

      return;
    }

    sourceFile$
      .pipe(
        switchMap((sourceFile) => {
          const payload = this.buildPayload(sourceFile, scheduleAtLocal);
          return this.documentApi.createDocument(payload);
        }),
        switchMap((document) => this.documentApi.sendDocument(document._id, this.toIso(scheduleAtLocal))),
        finalize(() => {
          this.sending = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          void this.router.navigate(['/documents']);
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to send document',
          });
          this.formError = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  private resolveSourceFileForPayload() {
    if (this.sourceType === 'template') {
      const sourceFile = this.uploadedSourceFile ?? this.selectedTemplate?.sourceFile;
      if (!sourceFile) {
        return null;
      }

      return of(sourceFile);
    }

    if (!this.selectedFile) {
      return null;
    }

    return this.templateApi.uploadPdf(this.selectedFile);
  }

  private resolveSourceFileForSave() {
    if (!this.isEditMode) {
      return this.resolveSourceFileForPayload();
    }

    if (!this.sourceFileReplaced && this.uploadedSourceFile) {
      return of(this.uploadedSourceFile);
    }

    if (!this.selectedFile) {
      return null;
    }

    return this.templateApi.uploadPdf(this.selectedFile);
  }

  private buildPayload(sourceFile: UploadedPdfFile, _scheduleAtLocal: string | undefined): DocumentPayload {
    this.normalizeChoiceGroupMetadata();
    this.fields = this.dedupeFieldsByIdKeepLast(this.fields);

    return {
      sourceType: this.sourceType,
      templateId:
        this.sourceType === 'template'
          ? this.selectedTemplate?._id ?? this.editingSourceTemplateId ?? undefined
          : undefined,
      sourceFile,
      fields: this.buildFieldsPayload(),
      signingOrderEnabled: this.signingOrderEnabled,
      recipients: this.recipients.map((recipient, index) => ({
        id: recipient.id,
        roleId: recipient.roleId,
        roleName: recipient.roleName.trim(),
        name: recipient.name.trim(),
        email: recipient.email.trim().toLowerCase(),
        order: index + 1,
      })),
      ccEmails: this.parseCommaSeparated(this.cc),
      document: {
        title: this.documentTitle.trim(),
        message: this.documentMessage.trim() || undefined,
      },
      tags: this.parseCommaSeparated(this.tags),
      documentSettings: this.normalizedDocumentSettings(this.documentSettings),
      expiryAt: this.toIso(this.expiryAtLocal),
    };
  }

  private isStepOneInvalid(): boolean {
    if (this.sourceType === 'upload' && !this.selectedFile) {
      return true;
    }

    if (this.sourceType === 'template' && !this.selectedTemplateId) {
      return true;
    }

    if (!this.documentTitle.trim()) {
      return true;
    }

    return this.recipients.some(
      (recipient) => !recipient.name.trim() || !recipient.email.trim() || !this.isValidEmail(recipient.email),
    );
  }

  private isStepTwoInvalid(): boolean {
    const validationError = this.validateStepTwo();
    if (validationError) {
      this.formError = validationError;
      return true;
    }

    return false;
  }

  private validateStepTwo(): string | null {
    return validateFieldConfiguratorStep(
      this.fields.map((field) => this.toConfiguredField(field)),
      this.fieldConfiguratorActors,
      Math.max(this.pageCount, 1),
      {
        emptyFields: 'Add at least one field before sending.',
        missingActor: (actor) => `Add at least one field for ${actor.label}.`,
        unknownActor: 'One or more fields are assigned to an unknown recipient.',
        outOfBounds: (field) => `Field "${FIELD_LABELS[field.type]}" has an out-of-range page number.`,
      },
    );
  }

  private validateFieldSettings(field: ConfiguredDocumentField): string | null {
    const settings = normalizeFieldSettings(field.settings);

    if (settings.common.tabIndexMode === 'custom' && (!settings.common.tabIndex || settings.common.tabIndex < 1)) {
      return `Field "${FIELD_LABELS[field.type]}" has an invalid custom tab index.`;
    }

    if (field.type === 'dropdown') {
      const options = settings.dropdown.options;
      if (options.length === 0) {
        return 'Dropdown options must include at least one value.';
      }

      const defaultOption = settings.dropdown.defaultOption;
      if (defaultOption && !options.includes(defaultOption)) {
        return 'Dropdown default option must match one of the options.';
      }
    }

    if (field.type === 'checkbox') {
      const values = sanitizeOptionValues(settings.checkbox.values ?? [settings.checkbox.value ?? ''], 'Checkbox1');
      if (values.length === 0) {
        return 'Checkbox values must include at least one value.';
      }
    }

    if (field.type === 'radio') {
      const values = sanitizeOptionValues(settings.radio.values ?? [settings.radio.value ?? ''], 'Radio1');
      if (values.length === 0) {
        return 'Radio values must include at least one value.';
      }
    }

    return null;
  }

  private buildFieldsPayload(): DocumentFieldDraft[] {
    this.normalizeChoiceGroupMetadata();
    this.fields = this.dedupeFieldsByIdKeepLast(this.fields);

    return this.fields.map((field) => {
      const normalized = this.toConfiguredField(field);
      return {
        ...normalized,
        role: normalized.role,
        page: Math.max(1, Math.trunc(normalized.page || 1)),
        fieldKey: trimOrUndefined(normalized.fieldKey) ?? normalized.fieldKey,
        label: trimOrUndefined(normalized.label),
        placeholder: trimOrUndefined(normalized.placeholder),
        settings: normalizeFieldSettingsForType(normalized.type, normalized.settings),
      };
    });
  }

  private notifyParentDocumentConfigSaved(documentId: string, status: DocumentStatus): void {
    if (!window.parent || window.parent === window) {
      return;
    }

    window.parent.postMessage(
      {
        type: 'document-config.saved',
        documentId,
        status,
      },
      '*',
    );
  }

  private choiceGroupFieldIds(field: ConfiguredDocumentField): string[] {
    if (field.type === 'checkbox') {
      return this.groupContextForField(field, 'checkbox').fields.map((entry) => entry.id);
    }
    if (field.type === 'radio') {
      return this.groupContextForField(field, 'radio').fields.map((entry) => entry.id);
    }
    return [field.id];
  }

  private choiceGroupKey(field: ConfiguredDocumentField): string {
    if (field.type === 'checkbox') {
      return `checkbox:${this.resolveCheckboxGroupId(field)}`;
    }
    if (field.type === 'radio') {
      return `radio:${this.resolveRadioGroupName(field)}`;
    }
    return field.id;
  }

  private validateChoiceGroup(anchor: ConfiguredDocumentField): string | null {
    if (anchor.type !== 'checkbox' && anchor.type !== 'radio') {
      return null;
    }
    const groupIds = new Set(this.choiceGroupFieldIds(anchor));
    const groupFields = this.fields.map((field) => this.toConfiguredField(field)).filter((field) => groupIds.has(field.id));
    if (groupFields.length <= 1) {
      return null;
    }
    const first = groupFields[0]!;
    const sameRole = groupFields.every((field) => field.role === first.role);
    if (!sameRole) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must have the same recipient role.`;
    }
    const samePage = groupFields.every((field) => field.page === first.page);
    if (!samePage) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must stay on the same page.`;
    }
    const sameRequired = groupFields.every((field) => field.required === first.required);
    if (!sameRequired) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must share the same required setting.`;
    }
    if (anchor.type === 'radio') {
      const groupName = this.resolveRadioGroupName(anchor);
      if (!groupName) {
        return 'Radio group name is required for grouped radio options.';
      }
    }
    return null;
  }

  private normalizeChoiceGroupMetadata(): void {
    this.fields = this.dedupeFieldsByIdKeepLast(this.fields);

    const sortedChoices = this.fields
      .map((field) => this.toConfiguredField(field))
      .filter((field) => field.type === 'checkbox' || field.type === 'radio')
      .sort((a, b) => {
        if (a.page !== b.page) {
          return a.page - b.page;
        }
        if (a.role !== b.role) {
          return a.role.localeCompare(b.role);
        }
        return a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y;
      });

    if (sortedChoices.length === 0) {
      return;
    }

    const processed = new Set<string>();
    const updates = new Map<string, DocumentFieldDraft>();

    for (const field of sortedChoices) {
      if (processed.has(field.id)) {
        continue;
      }

      const type = field.type as 'checkbox' | 'radio';
      const context = this.groupContextForField(field, type);
      const targetGroupKey = context.groupKey;

      for (const memberRaw of context.fields) {
        const member = this.toConfiguredField(memberRaw);
        processed.add(member.id);
        const current = this.toConfiguredField(updates.get(member.id) ?? memberRaw);

        if (type === 'checkbox') {
          const explicit = trimOrUndefined(current.settings.checkbox.optionGroupId);
          if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
            continue;
          }
          updates.set(member.id, {
            ...current,
            settings: {
              ...current.settings,
              checkbox: {
                ...current.settings.checkbox,
                optionGroupId: targetGroupKey,
              },
            },
          });
          continue;
        }

        const explicit = trimOrUndefined(current.settings.radio.groupName);
        if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
          continue;
        }
        updates.set(member.id, {
          ...current,
          settings: {
            ...current.settings,
            radio: {
              ...current.settings.radio,
              groupName: targetGroupKey,
            },
          },
        });
      }
    }

    if (updates.size === 0) {
      return;
    }

    this.fields = this.fields.map((field) => updates.get(field.id) ?? field);
    this.fields = this.dedupeFieldsByIdKeepLast(this.fields);
  }

  private dedupeFieldsByIdKeepLast(fields: DocumentFieldDraft[]): DocumentFieldDraft[] {
    const lastIndexById = new Map<string, number>();
    fields.forEach((field, index) => {
      lastIndexById.set(field.id, index);
    });
    return fields.filter((field, index) => lastIndexById.get(field.id) === index);
  }

  private parseCommaSeparated(value: string): string[] {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry, index, array) => entry.length > 0 && array.indexOf(entry) === index);
  }

  onBrandSelectionChange(brandId: string): void {
    this.applyBrandIdToSettings(this.documentSettings, brandId);
  }

  private loadBrands(): void {
    this.brandsLoading = true;
    this.brandApi
      .listBrands()
      .pipe(
        finalize(() => {
          this.brandsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.brands = response.items;
          this.ensureBrandSelection(this.documentSettings);
          this.cdr.markForCheck();
        },
        error: () => {
          this.brands = [];
          this.cdr.markForCheck();
        },
      });
  }

  private defaultBrandId(): string | undefined {
    return this.brands.find((brand) => brand.isDefault)?.brandId ?? this.brands[0]?.brandId;
  }

  private applyBrandIdToSettings(settings: TemplateDocumentSettings, brandId: string | undefined): void {
    const normalized = brandId?.trim();
    const selectedBrand = normalized ? this.brands.find((brand) => brand.brandId === normalized) : undefined;
    settings.brandId = selectedBrand?.brandId;
    settings.brandName = selectedBrand?.name;
    settings.brand = undefined;
  }

  private ensureBrandSelection(settings: TemplateDocumentSettings): void {
    if (settings.brandId?.trim()) {
      this.applyBrandIdToSettings(settings, settings.brandId);
      return;
    }

    const fallbackBrandId = this.defaultBrandId();
    if (fallbackBrandId) {
      this.applyBrandIdToSettings(settings, fallbackBrandId);
      return;
    }

    settings.brandId = undefined;
    settings.brandName = settings.brandName?.trim() || settings.brand?.trim() || undefined;
    settings.brand = undefined;
  }

  private normalizedDocumentSettings(settings: TemplateDocumentSettings): TemplateDocumentSettings {
    return {
      brandId: settings.brandId?.trim() || undefined,
      brandName: settings.brandName?.trim() || settings.brand?.trim() || undefined,
      allowPrintAndSign: settings.allowPrintAndSign,
    };
  }

  private closeScheduleUi(): void {
    this.sendOptionsOpen = false;
    this.schedulePickerOpen = false;
    this.scheduleDateTimeModalOpen = false;
    this.scheduleConfirmModalOpen = false;
    this.pendingScheduleAtLocal = '';
    this.scheduleDateInput = '';
    this.scheduleTimeInput = '';

    if (this.isScheduleFlowError(this.formError)) {
      this.formError = '';
    }
  }

  private formatScheduleSlotTime(value: Date): string {
    return this.userDateTimeFormat.formatMonthDayTime(value);
  }

  private formatScheduleSummaryTime(value: Date): string {
    return this.userDateTimeFormat.formatDateTime(value, { includeTimeZoneName: true });
  }

  private toLocalDateTimeInput(value: Date): string {
    return this.userDateTimeFormat.formatDateTimeInput(value);
  }

  private composeLocalDateTime(dateValue: string, timeValue: string): string | null {
    const date = dateValue.trim();
    const time = timeValue.trim();
    if (!date || !time) {
      return null;
    }

    return `${date}T${time}`;
  }

  private parseLocalDateTime(localDateTime: string): Date | null {
    return this.userDateTimeFormat.parseDateTimeInput(localDateTime);
  }

  private parseExpiryAtLocal(): Date | null {
    if (!this.expiryAtLocal) {
      return null;
    }

    return this.userDateTimeFormat.parseDateTimeInput(this.expiryAtLocal);
  }

  private validateScheduleAtLocal(localDateTime: string): string | null {
    const scheduledAt = this.parseLocalDateTime(localDateTime);
    if (!scheduledAt) {
      return SCHEDULE_REQUIRED_ERROR;
    }

    const minAllowedTime = Date.now() + this.minScheduleLeadMinutes * 60 * 1000;
    if (scheduledAt.getTime() < minAllowedTime) {
      return `The document's scheduled sending time must be at least ${this.minScheduleLeadMinutes} minutes ahead of the current time and before the document's expiry time`;
    }

    const expiryAt = this.parseExpiryAtLocal();
    if (expiryAt && scheduledAt.getTime() >= expiryAt.getTime()) {
      return SCHEDULE_EXPIRY_ERROR;
    }

    return null;
  }

  private isScheduleFlowError(message: string): boolean {
    return (
      message === SCHEDULE_REQUIRED_ERROR ||
      message === SCHEDULE_EXPIRY_ERROR ||
      message.startsWith("The document's scheduled sending time must be at least")
    );
  }

  private toIso(localDateTime: string | undefined): string | undefined {
    if (!localDateTime) {
      return undefined;
    }

    return this.userDateTimeFormat.parseDateTimeInput(localDateTime)?.toISOString();
  }

  private toLocalDateTimeForInput(value: string | undefined): string {
    if (!value) {
      return '';
    }

    return this.userDateTimeFormat.formatDateTimeInput(value);
  }

  private currentScheduleDate(): { year: number; month: number; day: number } {
    const inputValue = this.userDateTimeFormat.formatDateTimeInput(new Date());
    const [dateValue] = inputValue.split('T');
    return this.parseDateParts(dateValue ?? '');
  }

  private parseDateParts(value: string): { year: number; month: number; day: number } {
    const [yearText = '0', monthText = '0', dayText = '0'] = value.split('-');
    return {
      year: Number(yearText),
      month: Number(monthText),
      day: Number(dayText),
    };
  }

  private shiftDateParts(
    value: { year: number; month: number; day: number },
    days: number,
  ): { year: number; month: number; day: number } {
    const shifted = new Date(Date.UTC(value.year, value.month - 1, value.day));
    shifted.setUTCDate(shifted.getUTCDate() + days);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
    };
  }

  private weekdayFromDateParts(value: { year: number; month: number; day: number }): number {
    return new Date(Date.UTC(value.year, value.month - 1, value.day)).getUTCDay();
  }

  private scheduleDateForParts(value: { year: number; month: number; day: number }, hour: number, minute: number): Date {
    const dateText = `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
    const timeText = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    return this.parseLocalDateTime(`${dateText}T${timeText}`) ?? new Date();
  }

  private loadTemplates(search = ''): void {
    const requestId = ++this.templateSearchRequestId;
    this.templatesLoading = true;
    this.templateSearchError = '';

    this.templateApi
      .listTemplatesPage(1, 100, search)
      .pipe(
        finalize(() => {
          if (requestId !== this.templateSearchRequestId) {
            return;
          }

          this.templatesLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestId !== this.templateSearchRequestId) {
            return;
          }

          this.feedback.clearError();
          this.templates = response.items;
          this.templateSearchError = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          if (requestId !== this.templateSearchRequestId) {
            return;
          }

          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load templates',
          });
          this.templates = [];
          this.templateSearchError = normalized.message;
          this.cdr.markForCheck();
        },
      });
  }

  private reindexRecipients(): void {
    this.recipients = this.recipients.map((recipient, index) => ({
      ...recipient,
      order: index + 1,
    }));
  }

  private createGeneratedRecipient(): RecipientDraft {
    const roleName = `Recipient ${this.nextGeneratedRecipientRoleIndex}`;
    this.nextGeneratedRecipientRoleIndex += 1;
    return createRecipient(roleName, false);
  }

  private isValidEmail(value: string): boolean {
    return EMAIL_PATTERN.test(value.trim());
  }

  private toConfiguredField(field: DocumentFieldDraft): ConfiguredDocumentField {
    return {
      ...field,
      fieldKey: this.normalizedFieldKey(field),
      label: trimOrUndefined(field.label),
      placeholder: trimOrUndefined(field.placeholder),
      settings: normalizeFieldSettingsForType(field.type, field.settings),
    };
  }

  private normalizedFieldKey(field: Pick<DocumentFieldDraft, 'id' | 'type' | 'fieldKey'>): string {
    const trimmed = trimOrUndefined(field.fieldKey);
    if (trimmed) {
      return trimmed;
    }

    const prefix = defaultFieldKeyPrefix(field.type);
    const index = this.fields.filter((entry) => entry.type === field.type).findIndex((entry) => entry.id === field.id);
    if (index >= 0) {
      return `${prefix}${index + 1}`;
    }

    return `${prefix}1`;
  }

  private defaultRectByType(type: DocumentFieldType): DocumentFieldDraft['rect'] {
    if (type === 'signature') {
      return { x: 0.12, y: 0.12, width: 0.28, height: COMPACT_FIELD_HEIGHT };
    }

    if (type === 'image') {
      return { x: 0.12, y: 0.26, width: 0.24, height: COMPACT_FIELD_HEIGHT };
    }

    if (type === 'date_signed' || type === 'editable_date') {
      const dateText = this.currentUserDateSettings().dateFormat;
      return {
        x: 0.12,
        y: 0.26,
        width: textWidthToRect(dateText, { min: 0.12, max: 0.2, base: 0.04, perChar: 0.01 }),
        height: COMPACT_FIELD_HEIGHT,
      };
    }

    if (type === 'checkbox' || type === 'radio') {
      const optionText =
        type === 'checkbox'
          ? (createDefaultCheckboxSettings().value ?? 'Checkbox1')
          : (createDefaultRadioSettings().value ?? 'Radio1');
      return {
        x: 0.12,
        y: 0.26,
        width: textWidthToRect(optionText, { min: 0.065, max: 0.14, base: 0.03, perChar: 0.0075 }),
        height: COMPACT_FIELD_HEIGHT,
      };
    }

    const labelText = FIELD_LABELS[type];
    const width =
      type === 'dropdown'
        ? textWidthToRect(labelText, { min: 0.105, max: 0.22, base: 0.048, perChar: 0.0084 })
        : textWidthToRect(labelText, { min: 0.095, max: 0.2, base: 0.04, perChar: 0.008 });

    return { x: 0.12, y: 0.36, width, height: COMPACT_FIELD_HEIGHT };
  }

  private resolvePageElement(pageNumber: number): HTMLElement | null {
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return null;
    }

    return this.viewerHost?.nativeElement.querySelector<HTMLElement>(`.page[data-page-number="${pageNumber}"]`) ?? null;
  }

  private computeFitRectForNewField(
    type: DocumentFieldType,
    roleId: string,
    page: number,
    pageElement: HTMLElement | null,
  ): DocumentFieldDraft['rect'] | null {
    if (!pageElement) {
      return this.defaultRectByType(type);
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return this.defaultRectByType(type);
    }

    const probeField = this.toConfiguredField({
      id: 'field-measure',
      fieldKey: 'FieldMeasure',
      type,
      role: roleId,
      page,
      rect: { x: 0, y: 0, width: 0.1, height: COMPACT_FIELD_HEIGHT },
      required: true,
      label: FIELD_LABELS[type],
      settings: this.createFieldSettingsWithUserDefaults(),
    });

    return this.measureFieldRectFromRenderedContent(probeField, pageElement) ?? this.defaultRectByType(type);
  }

  private measureFieldRectFromRenderedContent(
    field: ConfiguredDocumentField,
    pageElement: HTMLElement | null,
  ): DocumentFieldDraft['rect'] | null {
    if (!pageElement) {
      return null;
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return null;
    }

    const probe = document.createElement('div');
    probe.className = `template-field template-field--${field.type} template-field--measure`;
    probe.style.position = 'absolute';
    probe.style.left = '-99999px';
    probe.style.top = '-99999px';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.zIndex = '-1';

    const surface = document.createElement('div');
    surface.className = 'template-field__surface';
    surface.append(this.buildFieldPlaceholderContent(field));
    probe.append(surface);
    pageElement.append(probe);

    const measured = surface.getBoundingClientRect();
    probe.remove();

    if (measured.width <= 0 || measured.height <= 0) {
      return null;
    }

    const width = clampNormalizedSize(
      (measured.width + MEASURED_FIELD_SAFETY_PX) / pageRect.width,
      MIN_MEASURED_FIELD_WIDTH,
      MAX_MEASURED_FIELD_WIDTH,
    );
    const height = clampNormalizedSize(
      (measured.height + MEASURED_FIELD_SAFETY_PX) / pageRect.height,
      MIN_MEASURED_FIELD_HEIGHT,
      MAX_MEASURED_FIELD_HEIGHT,
    );

    return { x: 0.12, y: 0.36, width, height };
  }

  private normalizeExistingFieldRectsToContentFit(): void {
    if (this.currentStep !== 2 || this.stepTwoMode !== 'configure' || this.fieldRectsNormalizedForSession) {
      return;
    }
    if (this.fields.length === 0) {
      this.fieldRectsNormalizedForSession = true;
      return;
    }

    let allMeasured = true;
    let anyChanged = false;

    const normalized = this.fields.map((field) => {
      const pageElement = this.resolvePageElement(field.page);
      const measured = this.measureFieldRectFromRenderedContent(this.toConfiguredField(field), pageElement);
      if (!measured) {
        allMeasured = false;
        return field;
      }

      const nextRect = {
        ...field.rect,
        width: measured.width,
        height: measured.height,
      };
      const clampedRect = this.clampRectToPage(nextRect);
      if (this.wouldOverlapField(field.id, field.page, clampedRect)) {
        return field;
      }

      if (
        Math.abs(clampedRect.width - field.rect.width) > 0.0001 ||
        Math.abs(clampedRect.height - field.rect.height) > 0.0001 ||
        Math.abs(clampedRect.x - field.rect.x) > 0.0001 ||
        Math.abs(clampedRect.y - field.rect.y) > 0.0001
      ) {
        anyChanged = true;
        return { ...field, rect: clampedRect };
      }

      return field;
    });

    this.fieldRectsNormalizedForSession = allMeasured;
    if (anyChanged) {
      this.fields = normalized;
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
    }
  }

  private createField(
    type: DocumentFieldType,
    roleId: string,
    page: number,
    rect?: DocumentFieldDraft['rect'],
  ): DocumentFieldDraft {
    const fieldKey = this.nextFieldKey(type);
    const settings = this.createFieldSettingsWithUserDefaults();
    if (type === 'checkbox') {
      settings.checkbox.optionGroupId = fieldKey;
    }
    if (type === 'radio') {
      settings.radio.groupName = fieldKey;
    }

    return {
      id: `field-${crypto.randomUUID()}`,
      fieldKey,
      type,
      role: roleId,
      page,
      rect: rect ?? this.defaultRectByType(type),
      required: true,
      label: FIELD_LABELS[type],
      settings,
    };
  }

  private currentUserDateSettings(): TemplateDateSettings {
    const defaults = this.userDateTimeFormat.dateFieldDefaults();
    return {
      dateFormat: defaults.dateFormat,
      timeFormat: defaults.timeFormat,
      defaultDate: undefined,
    };
  }

  private createFieldSettingsWithUserDefaults(): DocumentFieldSettings {
    return {
      ...createDefaultFieldSettings(),
      date: this.currentUserDateSettings(),
    };
  }

  private nextFieldKey(type: DocumentFieldType): string {
    const prefix = defaultFieldKeyPrefix(type);
    const count = this.fields.filter((field) => field.type === type).length + 1;
    return `${prefix}${count}`;
  }

  private resetStepTwoUiState(): void {
    this.stepTwoMode = 'configure';
    this.closeScheduleUi();
    this.activeRecipientRoleId = this.recipients[0]?.roleId ?? '';
    this.previewRecipientFilter = 'all';
    this.activePage = 1;
    this.pageCount = Math.max(this.uploadedSourceFile?.pageCount ?? this.selectedTemplate?.sourceFile.pageCount ?? 1, 1);
    this.selectedFieldId = null;
    this.dragFieldType = null;
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.viewerMounted = false;
    this.fieldRectsNormalizedForSession = false;
    this.clearFieldInteractionState();
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private clampRectToPage(rect: DocumentFieldDraft['rect']): DocumentFieldDraft['rect'] {
    const width = this.clamp(rect.width, 0.0001, 1);
    const height = this.clamp(rect.height, 0.0001, 1);
    const x = this.clamp(rect.x, 0, 1 - width);
    const y = this.clamp(rect.y, 0, 1 - height);
    return { x, y, width, height };
  }

  private rectsOverlap(a: DocumentFieldDraft['rect'], b: DocumentFieldDraft['rect']): boolean {
    const epsilon = 0.0001;
    return (
      a.x + a.width > b.x + epsilon &&
      b.x + b.width > a.x + epsilon &&
      a.y + a.height > b.y + epsilon &&
      b.y + b.height > a.y + epsilon
    );
  }

  private wouldOverlapField(fieldId: string | null, page: number, candidate: DocumentFieldDraft['rect']): boolean {
    return this.fields.some((field) => {
      if (field.page !== page) {
        return false;
      }
      if (fieldId && field.id === fieldId) {
        return false;
      }
      return this.rectsOverlap(field.rect, candidate);
    });
  }

  private isRectInvalid(rect: DocumentFieldDraft['rect']): boolean {
    if (rect.x < 0 || rect.x > 1 || rect.y < 0 || rect.y > 1) {
      return true;
    }

    if (rect.width <= 0 || rect.width > 1 || rect.height <= 0 || rect.height > 1) {
      return true;
    }

    if (rect.x + rect.width > 1 || rect.y + rect.height > 1) {
      return true;
    }

    return false;
  }

  private syncRoleColorRegistry(): void {
    const activeRoleIds = new Set(this.recipients.map((recipient) => recipient.roleId));

    for (const [roleId, color] of Array.from(this.roleColorRegistry.entries())) {
      if (activeRoleIds.has(roleId)) {
        continue;
      }

      this.roleColorRegistry.delete(roleId);
      if (!ROLE_COLOR_PALETTE.includes(color)) {
        this.releasedGeneratedRoleColors.push(color);
      }
    }

    for (const recipient of this.recipients) {
      if (this.roleColorRegistry.has(recipient.roleId)) {
        continue;
      }

      this.roleColorRegistry.set(recipient.roleId, this.nextRoleColor());
    }
  }

  private nextRoleColor(): string {
    const usedColors = new Set(this.roleColorRegistry.values());

    for (const paletteColor of ROLE_COLOR_PALETTE) {
      if (!usedColors.has(paletteColor)) {
        return paletteColor;
      }
    }

    while (this.releasedGeneratedRoleColors.length > 0) {
      const recycledColor = this.releasedGeneratedRoleColors.shift();
      if (recycledColor && !usedColors.has(recycledColor)) {
        return recycledColor;
      }
    }

    let nextColor = this.generateDynamicRoleColor(this.roleColorGeneratedIndex);
    this.roleColorGeneratedIndex += 1;

    while (usedColors.has(nextColor)) {
      nextColor = this.generateDynamicRoleColor(this.roleColorGeneratedIndex);
      this.roleColorGeneratedIndex += 1;
    }

    return nextColor;
  }

  private generateDynamicRoleColor(index: number): string {
    const hue = (ROLE_COLOR_HUE_OFFSET_DEGREES + index * ROLE_COLOR_GOLDEN_ANGLE_DEGREES) % 360;
    return `hsl(${Math.round(hue)}, 72%, 78%)`;
  }

  private scheduleOverlaySync(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
    }

    this.overlaySyncTimer = setTimeout(() => {
      this.syncOverlays();
    }, 0);
  }

  private syncOverlays(): void {
    const host = this.viewerHost?.nativeElement;
    if (!host || !this.viewerPdfSource || !this.viewerMounted) {
      return;
    }

    const pages = Array.from(host.querySelectorAll<HTMLElement>('.page[data-page-number]'));
    for (const pageElement of pages) {
      pageElement.style.position = 'relative';

      const pageNumber = Number(pageElement.dataset['pageNumber']);
      if (!Number.isInteger(pageNumber) || pageNumber < 1) {
        continue;
      }

      const overlayLayer = this.ensureOverlayLayer(pageElement, pageNumber);
      overlayLayer.classList.toggle(
        'template-overlay-layer--droppable',
        this.stepTwoMode === 'configure' && this.dragFieldType !== null && this.canPlaceFields,
      );
      overlayLayer.classList.toggle('template-overlay-layer--interacting', pageNumber === this.interactingOverlayPage);

      if (!this.canPlaceFields) {
        overlayLayer.classList.remove('template-overlay-layer--drop-hover');
      }

      overlayLayer.replaceChildren();

      const pageFields = this.fields.filter((field) => field.page === pageNumber && this.isFieldVisible(field));
      for (const field of pageFields) {
        overlayLayer.append(this.buildFieldNode(this.toConfiguredField(field)));
      }
    }
  }

  private ensureOverlayLayer(pageElement: HTMLElement, pageNumber: number): HTMLElement {
    const existing = pageElement.querySelector<HTMLElement>(':scope > .template-overlay-layer');
    if (existing) {
      existing.dataset['pageNumber'] = String(pageNumber);
      return existing;
    }

    const layer = document.createElement('div');
    layer.className = 'template-overlay-layer';
    layer.dataset['pageNumber'] = String(pageNumber);
    layer.addEventListener('dragover', this.onOverlayDragOver);
    layer.addEventListener('dragleave', this.onOverlayDragLeave);
    layer.addEventListener('drop', this.onOverlayDrop);
    pageElement.append(layer);
    return layer;
  }

  private isFieldVisible(field: DocumentFieldDraft): boolean {
    if (this.stepTwoMode === 'configure') {
      return true;
    }

    if (this.previewRecipientFilter === 'all') {
      return true;
    }

    return field.role === this.previewRecipientFilter;
  }

  private buildFieldNode(field: ConfiguredDocumentField): HTMLElement {
    const element = document.createElement('div');
    element.dataset['fieldId'] = field.id;
    element.className = `template-field template-field--${field.type}`;
    element.setAttribute('aria-label', `${FIELD_LABELS[field.type]} field`);

    if (field.id === this.selectedFieldId) {
      element.classList.add('template-field--selected');
    }

    if (field.id === this.interactingFieldId) {
      element.classList.add('template-field--interacting');
    }

    if (this.stepTwoMode !== 'configure') {
      element.classList.add('template-field--readonly');
    }

    const surface = document.createElement('div');
    surface.className = 'template-field__surface';
    if (field.type === 'signature' && this.signatureFramePreviewSettings.enabled) {
      surface.classList.add('template-field__surface--signature-frame');
    }
    surface.append(this.buildFieldPlaceholderContent(field));
    element.append(surface);

    if (this.stepTwoMode === 'configure' && this.canMutateExistingFields()) {
      element.append(this.buildFieldDeleteButton(field.id));
      if (field.id === this.selectedFieldId && this.isFieldResizable(field.type)) {
        element.append(this.buildFieldResizeGrip());
      }
    }

    element.style.setProperty('--role-accent', this.roleChipColor(field.role));
    this.applyFieldRect(element, field.rect);

    element.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.stepTwoMode === 'configure') {
        this.selectField(field.id);
      }
    });

    if (this.stepTwoMode === 'configure' && this.canMutateExistingFields()) {
      this.attachInteractHandlers(element, field.type);
    }

    return element;
  }

  private buildFieldPlaceholderContent(field: ConfiguredDocumentField): HTMLElement {
    const settings = normalizeFieldSettings(field.settings);
    const content = document.createElement('div');
    content.className = 'template-field__content';
    content.style.fontFamily = settings.font.family;
    content.style.fontSize = `${settings.font.size}px`;
    content.style.lineHeight = `${settings.font.lineHeight}px`;
    content.style.fontWeight = settings.font.bold ? '700' : '500';
    content.style.fontStyle = settings.font.italic ? 'italic' : 'normal';
    content.style.textDecoration = settings.font.underline ? 'underline' : 'none';
    content.style.color = settings.font.color || '#143459';
    content.style.textAlign = settings.font.align;
    content.style.letterSpacing = field.type === 'name' ? '' : `${settings.font.characterSpacing}px`;
    content.style.direction = settings.font.rtl ? 'rtl' : 'ltr';

    const requiredMarker = field.required ? '*' : '';
    const displayLabel = trimOrUndefined(field.label) ?? trimOrUndefined(field.fieldKey) ?? FIELD_LABELS[field.type];

    if (field.type === 'signature' && this.signatureFramePreviewSettings.enabled) {
      return this.buildSignatureFramePlaceholder();
    }

    if (field.type === 'dropdown') {
      const label = document.createElement('span');
      label.className = 'template-field__label';
      label.textContent = `${displayLabel}${requiredMarker}`;
      const caret = document.createElement('span');
      caret.className = 'template-field__caret';
      caret.textContent = '▾';
      content.append(label, caret);
      return content;
    }

    if (field.type === 'checkbox') {
      const row = document.createElement('span');
      row.className = 'template-field__choice';
      const box = document.createElement('span');
      box.className = 'template-field__choice-mark';
      row.append(box);
      content.append(row);
      return content;
    }

    if (field.type === 'radio') {
      const row = document.createElement('span');
      row.className = 'template-field__choice';
      const box = document.createElement('span');
      box.className = 'template-field__choice-mark template-field__choice-mark--radio';
      row.append(box);
      content.append(row);
      return content;
    }

    if (field.type === 'image') {
      const icon = document.createElement('span');
      icon.className = 'template-field__image-icon';
      icon.textContent = 'IMG';
      const label = document.createElement('span');
      label.className = 'template-field__label';
      label.textContent = `${trimOrUndefined(settings.image.title) ?? displayLabel}${requiredMarker}`;
      content.append(icon, label);
      return content;
    }

    if (field.type === 'date_signed' || field.type === 'editable_date') {
      const dateText = document.createElement('span');
      dateText.className = 'template-field__label';
      dateText.textContent = `${settings.date.dateFormat}${requiredMarker}`;
      content.append(dateText);
      return content;
    }

    if (field.type === 'textbox') {
      const label = document.createElement('span');
      label.className = 'template-field__label';
      const fallback = trimOrUndefined(settings.textbox.hintText) ?? trimOrUndefined(settings.textbox.defaultText) ?? displayLabel;
      label.textContent = `${fallback}${requiredMarker}`;
      content.append(label);
      return content;
    }

    const label = document.createElement('span');
    label.className = 'template-field__label';
    label.textContent = `${displayLabel}${requiredMarker}`;
    content.append(label);
    return content;
  }

  private get signatureFramePreviewSettings(): BrandSignatureFrameSettings {
    const selectedBrandId = this.documentSettings.brandId?.trim();
    if (!selectedBrandId) {
      return DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
    }

    return (
      this.brands.find((brand) => brand.brandId === selectedBrandId)?.signatureFrame ??
      DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS
    );
  }

  private buildSignatureFramePlaceholder(): HTMLElement {
    const settings = this.signatureFramePreviewSettings;
    const content = document.createElement('div');
    content.className = 'template-field__content template-field__signature-frame';

    const header = document.createElement('span');
    header.className = 'template-field__signature-frame-header';
    header.textContent = settings.showTimestamp ? 'XlhireEsigns · Jul 21, 2026, 06:09 PM' : 'XlhireEsigns';
    content.append(header);

    const box = document.createElement('div');
    box.className = 'template-field__signature-frame-box';
    const boxBody = document.createElement('div');
    boxBody.className = 'template-field__signature-frame-box-body';

    const signature = document.createElement('span');
    signature.className = 'template-field__signature-script';
    signature.textContent = 'John Doe';
    boxBody.append(signature);
    box.append(boxBody);
    content.append(box);

    const footer = document.createElement('div');
    footer.className = 'template-field__signature-frame-footer';

    if (settings.showRecipientName) {
      const name = document.createElement('span');
      name.className = 'template-field__signature-meta';
      name.textContent = 'John Doe';
      footer.append(name);
    }

    if (settings.showRecipientEmail) {
      const email = document.createElement('span');
      email.className = 'template-field__signature-meta';
      email.textContent = 'johndoe@example.com';
      footer.append(email);
    }

    const documentId = document.createElement('span');
    documentId.className = 'template-field__signature-docid';
    documentId.textContent = '1fc5d60f-46ca-41b9-a5d6-fad53595bf43';
    footer.append(documentId);

    content.append(footer);
    return content;
  }

  private buildFieldDeleteButton(fieldId: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'template-field__delete';
    button.setAttribute('aria-label', 'Remove field');
    button.innerHTML = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';

    const swallowPointerDown = (event: Event): void => {
      event.stopPropagation();
    };

    button.addEventListener('pointerdown', swallowPointerDown);
    button.addEventListener('mousedown', swallowPointerDown);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeField(fieldId);
    });

    return button;
  }

  private buildFieldResizeGrip(): HTMLElement {
    const grip = document.createElement('span');
    grip.className = 'template-field__resize-grip';
    grip.setAttribute('aria-hidden', 'true');
    return grip;
  }

  private attachInteractHandlers(element: HTMLElement, fieldType: DocumentFieldType): void {
    interact(element).unset();

    const interactable = interact(element);
    interactable.preventDefault('always');
    interactable.draggable({
      allowFrom: '.template-field__surface',
      ignoreFrom: '.template-field__delete, .template-field__resize-grip',
      listeners: {
        start: (event: any) => {
          this.zone.run(() => {
            this.beginFieldInteraction(event.target as HTMLElement);
          });
        },
        move: (event: any) => {
          this.zone.run(() => {
            this.onDragMove(event as { target: HTMLElement; dx: number; dy: number });
          });
        },
        end: (event: any) => {
          this.zone.run(() => {
            this.endFieldInteraction(event.target as HTMLElement);
          });
        },
      },
    });

    if (this.isFieldResizable(fieldType)) {
      interactable.resizable({
        allowFrom: '.template-field__resize-grip',
        edges: {
          left: false,
          right: '.template-field__resize-grip',
          top: false,
          bottom: '.template-field__resize-grip',
        },
        margin: 0,
        ignoreFrom: '.template-field__delete',
        listeners: {
          start: (event: any) => {
            this.zone.run(() => {
              this.beginFieldInteraction(event.target as HTMLElement);
            });
          },
          move: (event: any) => {
            this.zone.run(() => {
              this.onResizeMove(
                event as {
                  target: HTMLElement;
                  rect: { width: number; height: number };
                  deltaRect: { left: number; top: number };
                },
              );
            });
          },
          end: (event: any) => {
            this.zone.run(() => {
              this.endFieldInteraction(event.target as HTMLElement);
            });
          },
        },
      });
    }

    interactable.styleCursor(false);
  }

  private beginFieldInteraction(target: HTMLElement): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const fieldElement = target.closest<HTMLElement>('.template-field');
    const pageElement = target.closest<HTMLElement>('.page[data-page-number]');
    const overlayLayer = pageElement?.querySelector<HTMLElement>(':scope > .template-overlay-layer') ?? null;
    const fieldId = fieldElement?.dataset['fieldId'] ?? null;
    const pageNumber = Number(pageElement?.dataset['pageNumber']);

    if (!fieldElement || !fieldId || !overlayLayer || !Number.isInteger(pageNumber) || pageNumber < 1) {
      return;
    }

    if (this.interactingFieldId !== fieldId || this.interactingOverlayPage !== pageNumber) {
      this.clearFieldInteractionState();
    }

    this.interactingFieldId = fieldId;
    this.interactingOverlayPage = pageNumber;
    fieldElement.classList.add('template-field--interacting');
    overlayLayer.classList.add('template-overlay-layer--interacting');
  }

  private endFieldInteraction(_target?: HTMLElement | null): void {
    this.clearFieldInteractionState();
  }

  private clearFieldInteractionState(): void {
    const host = this.viewerHost?.nativeElement;
    host?.querySelectorAll<HTMLElement>('.template-field--interacting').forEach((element) => {
      element.classList.remove('template-field--interacting');
    });
    host?.querySelectorAll<HTMLElement>('.template-overlay-layer--interacting').forEach((element) => {
      element.classList.remove('template-overlay-layer--interacting');
    });

    this.interactingFieldId = null;
    this.interactingOverlayPage = null;
  }

  private onDragMove(event: { target: HTMLElement; dx: number; dy: number }): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const fieldId = event.target.dataset['fieldId'];
    const pageElement = event.target.closest<HTMLElement>('.page[data-page-number]');
    if (!fieldId || !pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const currentField = this.fields.find((field) => field.id === fieldId);
    if (!currentField || pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const nextRect = this.clampRectToPage({
      ...currentField.rect,
      x: currentField.rect.x + event.dx / pageRect.width,
      y: currentField.rect.y + event.dy / pageRect.height,
    });
    if (this.wouldOverlapField(fieldId, currentField.page, nextRect)) {
      return;
    }

    this.patchField(fieldId, { rect: nextRect }, false);
    this.applyFieldRect(event.target, nextRect);
  }

  private onResizeMove(event: {
    target: HTMLElement;
    rect: { width: number; height: number };
    deltaRect: { left: number; top: number };
  }): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const fieldId = event.target.dataset['fieldId'];
    const pageElement = event.target.closest<HTMLElement>('.page[data-page-number]');
    if (!fieldId || !pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const currentField = this.fields.find((field) => field.id === fieldId);
    if (!currentField || pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }
    if (!this.isFieldResizable(currentField.type)) {
      return;
    }

    const minSize = 0.02;
    const width = this.clamp(event.rect.width / pageRect.width, minSize, 1);
    const height = this.clamp(event.rect.height / pageRect.height, minSize, 1);
    const nextRect = this.clampRectToPage({
      x: currentField.rect.x + event.deltaRect.left / pageRect.width,
      y: currentField.rect.y + event.deltaRect.top / pageRect.height,
      width,
      height,
    });
    if (this.wouldOverlapField(fieldId, currentField.page, nextRect)) {
      return;
    }

    this.patchField(fieldId, { rect: nextRect }, false);
    this.applyFieldRect(event.target, nextRect);
  }

  private isFieldResizable(type: DocumentFieldType): boolean {
    return !NON_RESIZABLE_FIELD_TYPES.has(type);
  }

  private updateSelectedField(patch: Partial<ConfiguredDocumentField>, syncOverlay = true): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    this.patchField(selected.id, patch, syncOverlay);
    this.cdr.markForCheck();
  }

  private updateSelectedFieldSettings(
    mutator: (settings: DocumentFieldSettings) => DocumentFieldSettings,
  ): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const nextSettings = normalizeFieldSettingsForType(selected.type, mutator(normalizeFieldSettings(selected.settings)));
    this.patchField(selected.id, { settings: nextSettings });
    this.cdr.markForCheck();
  }

  private patchField(fieldId: string, patch: Partial<ConfiguredDocumentField>, syncOverlay = true): void {
    if (!this.canMutateExistingFields()) {
      return;
    }

    this.fields = this.fields.map((field) => {
      if (field.id !== fieldId) {
        return field;
      }

      const merged = {
        ...this.toConfiguredField(field),
        ...patch,
      };

      return {
        ...merged,
        fieldKey: this.normalizedFieldKey(merged),
        settings: normalizeFieldSettingsForType(merged.type, merged.settings),
      };
    });

    if (syncOverlay) {
      this.scheduleOverlaySync();
    }
  }

  private applyFieldRect(element: HTMLElement, rect: DocumentFieldDraft['rect']): void {
    element.style.left = `${rect.x * 100}%`;
    element.style.top = `${rect.y * 100}%`;
    element.style.width = `${rect.width * 100}%`;
    element.style.height = `${rect.height * 100}%`;
  }

  private readonly onOverlayDragOver = (event: DragEvent): void => {
    if (!this.canPlaceFields) {
      return;
    }

    const type = this.resolveDraggedFieldType(event.dataTransfer);
    if (!type) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    (event.currentTarget as HTMLElement | null)?.classList.add('template-overlay-layer--drop-hover');
  };

  private readonly onOverlayDragLeave = (event: DragEvent): void => {
    (event.currentTarget as HTMLElement | null)?.classList.remove('template-overlay-layer--drop-hover');
  };

  private readonly onOverlayDrop = (event: DragEvent): void => {
    const targetLayer = event.currentTarget as HTMLElement | null;
    targetLayer?.classList.remove('template-overlay-layer--drop-hover');

    if (!this.canPlaceFields) {
      return;
    }

    const type = this.resolveDraggedFieldType(event.dataTransfer);
    if (!type || !targetLayer) {
      return;
    }

    event.preventDefault();
    this.dragFieldType = null;

    const pageElement = targetLayer.closest<HTMLElement>('.page[data-page-number]');
    if (!pageElement) {
      return;
    }

    const pageNumber = Number(targetLayer.dataset['pageNumber'] ?? pageElement.dataset['pageNumber']);
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const roleId = this.activeRecipientRoleId || this.recipients[0]?.roleId;
    if (!roleId) {
      this.formError = 'Add a recipient before configuring fields.';
      return;
    }

    const rectSeed = this.computeFitRectForNewField(type, roleId, pageNumber, pageElement);
    if (!rectSeed) {
      this.formError = 'Please wait for page rendering to finish, then drop the field again.';
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }
    const rawX = (event.clientX - pageRect.left) / pageRect.width;
    const rawY = (event.clientY - pageRect.top) / pageRect.height;
    const rect = this.clampRectToPage({
      ...rectSeed,
      x: rawX - rectSeed.width / 2,
      y: rawY - rectSeed.height / 2,
    });
    if (this.wouldOverlapField(null, pageNumber, rect)) {
      this.formError = "Can't place field on top of another field.";
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }

    const createdField = this.createField(type, roleId, pageNumber, rect);
    this.fields = [...this.fields, createdField];
    this.selectedFieldId = createdField.id;
    this.formError = '';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  };

  private clearDropHoverClasses(): void {
    this.viewerHost?.nativeElement
      .querySelectorAll<HTMLElement>('.template-overlay-layer--drop-hover')
      .forEach((layer) => layer.classList.remove('template-overlay-layer--drop-hover'));
  }

  private resolveDraggedFieldType(dataTransfer: DataTransfer | null): DocumentFieldType | null {
    const value = this.dragFieldType ?? dataTransfer?.getData('text/plain') ?? '';
    return this.normalizeFieldType(value);
  }

  private normalizeFieldType(value: string): DocumentFieldType | null {
    if (!value) {
      return null;
    }

    if (!TEMPLATE_FIELD_TYPES.includes(value as DocumentFieldType)) {
      return null;
    }

    return value as DocumentFieldType;
  }

  private extractPagesCount(event: unknown): number {
    if (!event || typeof event !== 'object') {
      return 0;
    }

    const direct = (event as { pagesCount?: unknown }).pagesCount;
    if (typeof direct === 'number' && Number.isInteger(direct) && direct > 0) {
      return direct;
    }

    const nested = (event as { pages?: unknown }).pages;
    if (typeof nested === 'number' && Number.isInteger(nested) && nested > 0) {
      return nested;
    }

    return 0;
  }

  private extractPageChange(event: unknown): number {
    if (typeof event === 'number' && Number.isInteger(event)) {
      return event;
    }

    if (!event || typeof event !== 'object') {
      return 0;
    }

    const eventNumber = (event as { pageNumber?: unknown }).pageNumber;
    if (typeof eventNumber === 'number' && Number.isInteger(eventNumber)) {
      return eventNumber;
    }

    const currentPageNumber = (event as { currentPageNumber?: unknown }).currentPageNumber;
    if (typeof currentPageNumber === 'number' && Number.isInteger(currentPageNumber)) {
      return currentPageNumber;
    }

    return 0;
  }

  private finalizePdfLoaded(): void {
    this.pdfLoading = false;
    this.pdfLoaded = true;
    this.pdfLoadError = '';
    this.viewerMounted = true;
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  private ensureViewerRotationReset(): void {
    if (this.viewerRotation === 0) {
      return;
    }

    this.viewerRotation = 0;
    this.cdr.markForCheck();
    this.scheduleOverlaySync();
  }

  private releasePreviewUrl(): void {
    if (!this.previewUrl) {
      return;
    }

    URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }
}
